<template>
  <div class="sales-dashboard">
    <!-- ===== LOADER ===== -->
    <div v-if="isLoading" class="global-loader">
      <cube-loader />
      <p class="loader-text">Loading Sales Data...</p>
    </div>

    <template v-else>
      <!-- ===== HERO HEADER ===== -->
      <header class="dash-hero">
        <div class="dash-hero-grid">
          <div class="dash-hero-main">
            <div class="dash-hero-title">SALES REPORTS</div>
            <div class="dash-hero-sub">Sales overview</div>
          </div>
          <div class="dash-hero-side">
            <div class="dash-hero-badge">
              <span class="badge-dot"></span><span>Live</span>
            </div>
            <div class="dash-hero-user">
              <b-icon-person-circle size="1.3rem" />
              <span>{{ userDetails.username || 'Guest' }}</span>
            </div>
          </div>
        </div>
      </header>

      <!-- ===== MAIN ===== -->
      <div class="main">
        <div class="breadcrumb">Reports <span>/ Sales overview</span></div>

        <div class="topbar">
          <h1>Sales overview</h1>
          <div class="controls">
            <button class="btn btn-ghost" @click="refreshData" :disabled="isRefreshing">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                   :class="{ 'spin': isRefreshing }">
                <path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
              </svg>
              Refresh
            </button>
            <button class="btn btn-primary" @click="openViewModal('daily')">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>
              </svg>
              View Details
            </button>
          </div>
        </div>

        <!-- ===== KPI ROW ===== -->
        <div class="kpi-grid">
          <div
            class="kpi"
            v-for="(period, idx) in periodCards"
            :key="period.key"
            :class="{ primary: period.key === selectedPeriodKey || (idx === 0 && !selectedPeriodKey) }"
            @click="selectPeriod(period.key)"
          >
            <div class="label">{{ period.label }}</div>
            <div class="amtrow">
              <div class="amount">{{ formatCurrency(period.totalSales) }}</div>
              <div class="trend" :class="trendClass(period.change)" v-if="period.change !== null">
                {{ period.change >= 0 ? '↑' : '↓' }}{{ Math.abs(period.change).toFixed(1) }}%
              </div>
            </div>
            <div class="kpi-meta">{{ period.orders || 0 }} transactions</div>
          </div>
        </div>

        <!-- ===== SECTION HEAD ===== -->
        <div class="section-head">
          <div>
            <h2 class="section-title">{{ selectedPeriod ? selectedPeriod.label : 'Period' }}, in detail</h2>
            <p class="section-sub">
              {{ selectedPeriod ? selectedPeriod.orders : 0 }} transactions
              <span v-if="selectedPeriod && selectedPeriod.loading"> · Loading…</span>
            </p>
          </div>
          <div
            class="section-link"
            v-if="selectedPeriod && selectedPeriod.detailsLoaded"
            @click="openPeriodModal(selectedPeriod.key)"
          >
            View full report →
          </div>
        </div>

        <!-- ===== GRID3 ===== -->
        <div class="grid3">
          <div class="panel">
            <div class="panel-title">Sales trend</div>
            <div class="panel-sub">Breakdown for {{ selectedPeriod ? selectedPeriod.label : 'selected period' }}</div>
            <div class="trend-wrap">
              <div class="trend-chart-wrapper main-trend">
                <canvas :id="'period_trend_chart_' + (selectedPeriod ? selectedPeriod.key : 'none')"></canvas>
              </div>
              <div class="trend-stats" v-if="selectedPeriod && selectedPeriod.detailsLoaded">
                <div class="trend-stat">
                  <div class="k">Total sales</div>
                  <div class="v">{{ formatCurrency(selectedPeriod.totalSales) }}</div>
                </div>
                <div class="trend-stat">
                  <div class="k">Net sales</div>
                  <div class="v">{{ formatCurrency(selectedPeriod.netSales) }}</div>
                </div>
                <div class="trend-stat">
                  <div class="k">Refunds</div>
                  <div class="v coral">{{ formatCurrency(selectedPeriod.totalRefund) }}</div>
                </div>
              </div>
              <div class="trend-legend" v-if="selectedPeriod && selectedPeriod.detailsLoaded">
                <span class="legend-item"><span class="legend-dot" style="background:#0D4F52"></span>Total Sales</span>
                <span class="legend-item"><span class="legend-dot" style="background:#1E8E5A"></span>Regular</span>
                <span class="legend-item"><span class="legend-dot" style="background:#B8862E"></span>Layaway</span>
                <span class="legend-item"><span class="legend-dot" style="background:#D64545"></span>Refunds</span>
              </div>
            </div>
          </div>

          <div class="panel">
            <div class="panel-title">Invoice categories</div>
            <div class="panel-sub">By invoice type</div>
            <div class="invoice-list" v-if="selectedPeriod && selectedPeriod.detailsLoaded">
              <div class="invoice-row">
                <div><div class="name">Discounts</div></div>
                <div class="val">{{ formatCurrency(selectedPeriod.regularSales) }}</div>
              </div>
              <div class="invoice-row">
                <div><div class="name">Warranties</div></div>
                <div class="val">{{ formatCurrency(selectedPeriod.layawayTotal - selectedPeriod.layawayPaid) }}</div>
              </div>
              <div class="invoice-row">
                <div><div class="name">Setups</div></div>
                <div class="val">{{ formatCurrency(selectedPeriod.layawayPaid) }}</div>
              </div>
              <div class="invoice-row" v-if="selectedPeriod.totalSpiff > 0">
                <div>
                  <div class="name">SPIFF</div>
                  <div class="meta">{{ selectedPeriod.spiffInvoiceCount || 0 }} invoices</div>
                </div>
                <div class="val spiff">{{ formatCurrency(selectedPeriod.totalSpiff) }}</div>
              </div>
              <div class="invoice-row">
                <div><div class="name">Total Refunds</div></div>
                <div class="val danger">{{ formatCurrency(selectedPeriod.totalRefund) }}</div>
              </div>
            </div>
            <div class="invoice-list" v-else>
              <div class="invoice-row"><div class="name muted">Select a period to view</div></div>
            </div>
          </div>

          <div class="panel">
            <div class="panel-title">At a glance</div>
            <div class="panel-sub">Key ratios for the period</div>
            <div class="stat-list" v-if="selectedPeriod && selectedPeriod.detailsLoaded">
              <div class="stat-row"><span class="k">Average transaction</span><span class="v">{{ avgOrderValue(selectedPeriod) }}</span></div>
              <div class="stat-row"><span class="k">Refund rate</span><span class="v danger">{{ refundRate(selectedPeriod) }}%</span></div>
              <div class="stat-row"><span class="k">Net sales</span><span class="v">{{ formatCurrency(selectedPeriod.netSales) }}</span></div>
              <div class="stat-row"><span class="k">Layaway opened</span><span class="v">{{ formatCurrency(selectedPeriod.layawayTotal - selectedPeriod.layawayPaid) }}</span></div>
              <div class="stat-row"><span class="k">Layaway collected</span><span class="v">{{ formatCurrency(selectedPeriod.layawayPaid) }}</span></div>
              <div class="stat-row"><span class="k">Total visits</span><span class="v">{{ selectedPeriod.visits.total || 0 }}</span></div>
              <div class="stat-row" v-if="selectedPeriod.totalSpiff > 0">
                <span class="k">SPIFF total</span><span class="v spiff">{{ formatCurrency(selectedPeriod.totalSpiff) }}</span>
              </div>
            </div>
            <div class="stat-list" v-else>
              <div class="stat-row"><span class="k muted">Select a period to view</span></div>
            </div>
          </div>
        </div>

        <!-- ===== BREAKDOWN ===== -->
        <div class="breakdown-grid">
          <div class="breakdown">
            <div class="panel-title">Payment methods</div>
            <div class="panel-sub">How sales were collected for {{ selectedPeriod ? selectedPeriod.label : 'this period' }}</div>

            <template v-if="selectedPeriod && selectedPeriod.detailsLoaded && selectedPeriod.paymentMethods && selectedPeriod.paymentMethods.length">
              <div class="payment-pie-layout">
                <div class="payment-pie-chart-wrap">
                  <canvas id="payment_pie_selected"></canvas>
                  <div class="pie-center">
                    <div class="pie-center-label">Total</div>
                    <div class="pie-center-value">{{ formatCurrency(paymentPieTotal) }}</div>
                  </div>
                </div>
                <div class="tender-legend-grid">
                  <div class="item" v-for="pm in selectedPeriod.paymentMethods.slice(0, 8)" :key="'leg-' + pm.method">
                    <span class="dot" :style="{ background: getColor(pm.method) }"></span>
                    <span class="leg-name">{{ pm.method }}</span>
                    <span class="pct">{{ (pm.percentage || 0).toFixed(1) }}%</span>
                  </div>
                </div>
              </div>
              <table>
                <thead><tr><th>Tender</th><th>Invoices</th><th>Amount</th></tr></thead>
                <tbody>
                  <tr v-for="pm in selectedPeriod.paymentMethods" :key="'row-' + pm.method">
                    <td>
                      <div class="row-label">
                        <span class="dot" :style="{ background: getColor(pm.method) }"></span>
                        {{ pm.method }}
                      </div>
                    </td>
                    <td>{{ pm.count || 0 }}</td>
                    <td>{{ formatCurrency(pm.amount || 0) }}</td>
                  </tr>
                </tbody>
              </table>
            </template>
            <p class="muted-note" v-else>Select a period to see payment methods.</p>
          </div>

          <div class="breakdown">
            <div class="panel-title">Net sales bridge</div>
            <div class="panel-sub">Gross sales less refunds for the period</div>

            <template v-if="selectedPeriod && selectedPeriod.detailsLoaded">
              <div class="bridge-chart-wrap">
                <canvas id="bridge_bar_chart"></canvas>
              </div>
              <div class="bridge-legend">
                <span class="legend-item"><span class="legend-dot" style="background:#0D4F52"></span>Gross sales</span>
                <span class="legend-item"><span class="legend-dot" style="background:#D64545"></span>Refunds</span>
                <span class="legend-item"><span class="legend-dot" style="background:#1E8E5A"></span>Net sales</span>
                <span class="legend-item"><span class="legend-dot" style="background:#B8862E"></span>Layaway</span>
              </div>
              <div class="bridge-numbers">
                <div class="bridge-col">
                  <div class="bridge-label">Gross sales</div>
                  <div class="bridge-value">{{ formatCurrency(selectedPeriod.totalSales) }}</div>
                </div>
                <div class="bridge-col mid">
                  <div class="bridge-label danger">Refunds ({{ refundRate(selectedPeriod) }}%)</div>
                  <div class="bridge-value danger">−{{ formatCurrency(selectedPeriod.totalRefund) }}</div>
                </div>
                <div class="bridge-col">
                  <div class="bridge-label">Net sales</div>
                  <div class="bridge-value net">{{ formatCurrency(selectedPeriod.netSales) }}</div>
                </div>
              </div>
              <div class="pipeline-row">
                <div class="pipeline-card">
                  <div class="k">New layaway</div>
                  <div class="v">{{ formatCurrency(selectedPeriod.layawayTotal - selectedPeriod.layawayPaid) }}</div>
                  <div class="note">In progress — not yet in net sales above</div>
                </div>
                <div class="pipeline-card">
                  <div class="k">Paid layaway</div>
                  <div class="v">{{ formatCurrency(selectedPeriod.layawayPaid) }}</div>
                  <div class="note">Collected — already counted in tender totals</div>
                </div>
              </div>
            </template>
            <p class="muted-note" v-else>Select a period to see the net sales bridge.</p>
          </div>
        </div>

        <!-- Quick-nav cards -->
        <div class="main-cards-grid">
          <div class="main-card" @click="openViewModal('daily')">
            <div class="main-card-icon daily">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            </div>
            <div class="main-card-content">
              <h6>Daily Sales</h6>
              <p>Day-by-day view with week &amp; month filters</p>
              <span class="main-card-cta">Explore →</span>
            </div>
          </div>
          <div class="main-card" @click="openViewModal('weekly')">
            <div class="main-card-icon weekly">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"/></svg>
            </div>
            <div class="main-card-content">
              <h6>Weekly Sales</h6>
              <p>Weekly totals with month &amp; year filters</p>
              <span class="main-card-cta">Explore →</span>
            </div>
          </div>
          <div class="main-card" @click="openViewModal('monthly')">
            <div class="main-card-icon monthly">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19V9M12 19V5M20 19v-7"/></svg>
            </div>
            <div class="main-card-content">
              <h6>Monthly Sales</h6>
              <p>Month-over-month trend by year</p>
              <span class="main-card-cta">Explore →</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ===== PERIOD DETAIL MODAL ===== -->
    <b-modal v-model="showDetailModal" :title="detailModalTitle" size="xl" hide-footer
             body-class="detail-modal-body" header-class="detail-modal-header" @hidden="detailModalData = null">
      <div v-if="isDetailModalLoading" class="modal-loader"><cube-loader small /><p class="loader-text">Loading details...</p></div>
      <div v-else-if="detailModalData">
        <div class="modal-summary-grid">
          <div class="modal-summary-item"><div class="summary-icon total"><b-icon-cash-stack /></div><div><span class="label">Total Sales</span><span class="value">{{ formatCurrency(detailModalData.totalSales) }}</span></div></div>
          <div class="modal-summary-item"><div class="summary-icon net"><b-icon-wallet /></div><div><span class="label">Net Sales</span><span class="value">{{ formatCurrency(detailModalData.netSales) }}</span></div></div>
          <div class="modal-summary-item"><div class="summary-icon regular"><b-icon-cart-plus /></div><div><span class="label">Regular Sales</span><span class="value">{{ formatCurrency(detailModalData.regularSales) }}</span></div></div>
          <div class="modal-summary-item"><div class="summary-icon layaway"><b-icon-clock-history /></div><div><span class="label">Layaway</span><span class="value">{{ formatCurrency(detailModalData.layawayTotal) }}</span><small>Paid: {{ formatCurrency(detailModalData.layawayPaid) }}</small></div></div>
          <div class="modal-summary-item"><div class="summary-icon refund"><b-icon-arrow-return-left /></div><div><span class="label">Total Refunds</span><span class="value danger">{{ formatCurrency(detailModalData.totalRefund) }}</span></div></div>
          <div class="modal-summary-item"><div class="summary-icon orders"><b-icon-receipt /></div><div><span class="label">Orders</span><span class="value">{{ detailModalData.totalOrders }}</span><small>Avg: {{ formatCurrency(detailModalData.avgOrder) }}</small></div></div>
        </div>

        <b-tabs pills card class="modal-tabs">
          <b-tab title="Payment Methods" active>
            <div class="payment-methods-grid">
              <div class="payment-method-card" v-for="pm in detailModalData.paymentMethods" :key="pm.method">
                <div class="payment-method-header">
                  <span class="payment-method-name"><span class="method-dot" :style="{ background: getColor(pm.method) }"></span>{{ pm.method }}</span>
                  <span class="payment-method-amount">{{ formatCurrency(pm.amount) }}</span>
                </div>
                <div class="payment-bar"><div class="payment-bar-fill" :style="{ width: pm.percentage + '%', background: getColor(pm.method) }"></div></div>
                <div class="payment-method-footer"><span>{{ pm.count }} transactions</span><span>{{ pm.percentage.toFixed(1) }}%</span></div>
                <div v-if="pm.subMethods && pm.subMethods.length" class="sub-methods">
                  <div v-for="sub in pm.subMethods" :key="sub.name" class="sub-method">
                    <span>↳ {{ sub.name }}</span><span>{{ formatCurrency(sub.amount) }}</span>
                  </div>
                </div>
              </div>
              <p class="muted-note" v-if="!detailModalData.paymentMethods || !detailModalData.paymentMethods.length">No payment data available</p>
            </div>
          </b-tab>

          <b-tab title="Salespeople">
            <div class="table-responsive-wrapper">
              <table class="modal-table">
                <thead><tr><th>Salesperson</th><th class="text-right">Total</th><th class="text-right">Net</th><th class="text-right">Regular</th><th class="text-right">Layaway</th><th class="text-right">Layaway Paid</th><th class="text-right">Refunds</th><th class="text-right">Orders</th><th class="text-right">SPIFF</th><th class="text-center">Action</th></tr></thead>
                <tbody>
                  <tr v-for="sp in detailModalData.salespeople" :key="sp.name">
                    <td><strong>{{ sp.name }}</strong></td>
                    <td class="text-right">{{ formatCurrency(sp.total_sales) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.net_sales) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.regular_sales) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.layaway_total) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.layaway_paid) }}</td>
                    <td class="text-right danger">{{ formatCurrency(sp.total_refund) }}</td>
                    <td class="text-right">{{ sp.orders || 0 }}</td>
                    <td class="text-right spiff-value">{{ formatCurrency(sp.spiff) }}</td>
                    <td class="text-center"><b-button size="sm" variant="outline-primary" @click="openSalespersonDetail(sp)"><b-icon-search size="sm" /></b-button></td>
                  </tr>
                </tbody>
                <tfoot><tr class="table-total"><td><strong>TOTAL</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(detailModalData.totalSales) }}</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(detailModalData.netSales) }}</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(detailModalData.regularSales) }}</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(detailModalData.layawayTotal) }}</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(detailModalData.layawayPaid) }}</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(detailModalData.totalRefund) }}</strong></td>
                  <td class="text-right"><strong>{{ detailModalData.totalOrders }}</strong></td>
                  <td class="text-right spiff-value"><strong>{{ formatCurrency(detailModalData.totalSpiff) }}</strong></td><td></td></tr></tfoot>
              </table>
            </div>
          </b-tab>

          <b-tab title="Locations">
            <div class="table-responsive-wrapper">
              <table class="modal-table">
                <thead><tr><th>Location</th><th class="text-right">Total</th><th class="text-right">Net</th><th class="text-right">Regular</th><th class="text-right">Layaway</th><th class="text-right">Layaway Paid</th><th class="text-right">Refunds</th><th class="text-right">Orders</th><th class="text-right">Visits</th><th class="text-right">SPIFF</th></tr></thead>
                <tbody>
                  <tr v-for="loc in detailModalData.locations" :key="loc.id">
                    <td>{{ loc.name }}</td>
                    <td class="text-right">{{ formatCurrency(loc.total_sales) }}</td>
                    <td class="text-right">{{ formatCurrency(loc.net_sales) }}</td>
                    <td class="text-right">{{ formatCurrency(loc.regular_sales) }}</td>
                    <td class="text-right">{{ formatCurrency(loc.layaway_total) }}</td>
                    <td class="text-right">{{ formatCurrency(loc.layaway_paid) }}</td>
                    <td class="text-right danger">{{ formatCurrency(loc.total_refund) }}</td>
                    <td class="text-right">{{ loc.orders || 0 }}</td>
                    <td class="text-right">{{ loc.visits || 0 }}</td>
                    <td class="text-right spiff-value">{{ formatCurrency(loc.spiff) }}</td>
                  </tr>
                </tbody>
                <tfoot><tr class="table-total"><td><strong>TOTAL</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(detailModalData.totalSales) }}</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(detailModalData.netSales) }}</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(detailModalData.regularSales) }}</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(detailModalData.layawayTotal) }}</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(detailModalData.layawayPaid) }}</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(detailModalData.totalRefund) }}</strong></td>
                  <td class="text-right"><strong>{{ detailModalData.totalOrders }}</strong></td>
                  <td class="text-right"><strong>{{ detailModalData.totalVisits }}</strong></td>
                  <td class="text-right spiff-value"><strong>{{ formatCurrency(detailModalData.totalSpiff) }}</strong></td></tr></tfoot>
              </table>
            </div>
          </b-tab>

          <b-tab title="Customer Visits">
            <div class="visits-detail-grid">
              <div class="visits-stats-grid">
                <div class="visits-stat-card"><span class="visits-stat-label">Total Visits</span><span class="visits-stat-value">{{ detailModalData.totalVisits }}</span></div>
                <div class="visits-stat-card"><span class="visits-stat-label">Unique Customers</span><span class="visits-stat-value">{{ detailModalData.uniqueCustomers }}</span></div>
                <div class="visits-stat-card"><span class="visits-stat-label">Completed</span><span class="visits-stat-value">{{ detailModalData.completedVisits }}</span></div>
                <div class="visits-stat-card"><span class="visits-stat-label">Active</span><span class="visits-stat-value">{{ detailModalData.activeVisits }}</span></div>
              </div>

              <div class="visits-action-breakdown" v-if="detailModalData.actionTypes && Object.keys(detailModalData.actionTypes).length">
                <h6 class="section-title">Action Type Breakdown</h6>
                <div class="action-type-grid">
                  <div class="action-type-card" v-for="(count, type) in detailModalData.actionTypes" :key="type">
                    <span class="action-type-name">{{ type.replace('_', ' ').toUpperCase() }}</span>
                    <span class="action-type-count">{{ count }}</span>
                    <div class="action-type-bar"><div class="action-type-bar-fill" :style="{ width: ((count / (detailModalData.totalVisits || 1)) * 100) + '%' }"></div></div>
                  </div>
                </div>
              </div>

              <div class="table-responsive-wrapper" style="max-height:300px;">
                <table class="modal-table">
                  <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Check In</th><th>Check Out</th><th>Action</th><th>Created By</th></tr></thead>
                  <tbody>
                    <tr v-for="visit in (detailModalData.visitList || [])" :key="visit.id">
                      <td><strong>{{ visit.name || 'N/A' }}</strong></td>
                      <td>{{ visit.email || 'N/A' }}</td>
                      <td>{{ visit.phone || 'N/A' }}</td>
                      <td>{{ formatDate(visit.check_in_time) }}</td>
                      <td>{{ visit.check_out_time ? formatDate(visit.check_out_time) : '-' }}</td>
                      <td><b-badge :variant="getActionTypeVariant(visit.action_type)" pill>{{ visit.action_type || 'unknown' }}</b-badge></td>
                      <td>{{ visit.created_by || 'N/A' }}</td>
                    </tr>
                    <tr v-if="!detailModalData.visitList || !detailModalData.visitList.length"><td colspan="7" class="text-center muted">No visit data available</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </b-tab>

          <b-tab title="SPIFF Details">
            <div v-if="detailModalData.spiff_summary && detailModalData.spiff_summary.total_spiff_value > 0">
              <div class="spiff-summary-cards">
                <div class="spiff-card"><span class="spiff-card-label">Total SPIFF</span><span class="spiff-card-value">{{ formatCurrency(detailModalData.spiff_summary.total_spiff_value) }}</span></div>
                <div class="spiff-card"><span class="spiff-card-label">SPIFF Invoices</span><span class="spiff-card-value">{{ detailModalData.spiff_summary.spiff_invoice_count }}</span></div>
                <div class="spiff-card"><span class="spiff-card-label">% of Sales</span><span class="spiff-card-value">{{ detailModalData.spiff_summary.spiff_percentage_of_sales || 0 }}%</span></div>
                <div class="spiff-card"><span class="spiff-card-label">Avg / Invoice</span><span class="spiff-card-value">{{ formatCurrency(detailModalData.spiff_summary.avg_spiff_per_invoice) }}</span></div>
                <div class="spiff-card"><span class="spiff-card-label">Unique Products</span><span class="spiff-card-value">{{ detailModalData.spiff_summary.unique_spiff_products || 0 }}</span></div>
                <div class="spiff-card"><span class="spiff-card-label">Salespeople</span><span class="spiff-card-value">{{ detailModalData.spiff_summary.salespeople_with_spiffs || 0 }}</span></div>
              </div>
              <div v-if="detailModalData.spiff_summary.spiff_invoice_details && detailModalData.spiff_summary.spiff_invoice_details.length" class="spiff-invoice-table">
                <h6 class="section-title">SPIFF Invoice Details</h6>
                <div class="table-responsive-wrapper" style="max-height:300px;">
                  <table class="modal-table">
                    <thead><tr><th>Invoice</th><th>Salesperson</th><th>Location</th><th>Date</th><th class="text-right">Invoice Total</th><th class="text-right">SPIFF Base</th><th class="text-right">SPIFF Value</th><th class="text-right">Products</th></tr></thead>
                    <tbody>
                      <tr v-for="inv in detailModalData.spiff_summary.spiff_invoice_details" :key="inv.invoice_id">
                        <td><strong>{{ inv.invoice_id }}</strong></td>
                        <td>{{ inv.username }}</td>
                        <td>{{ getLocationName(inv.location_id) }}</td>
                        <td>{{ formatDate(inv.sale_date) }}</td>
                        <td class="text-right">{{ formatCurrency(inv.invoice_total) }}</td>
                        <td class="text-right">{{ formatCurrency(inv.spiff_base_amount) }}</td>
                        <td class="text-right spiff-value">{{ formatCurrency(inv.spiff_value) }}</td>
                        <td class="text-right">{{ inv.spiff_products_count || 0 }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <p class="muted-note text-center" v-else>No SPIFF data available for this period</p>
          </b-tab>

          <b-tab title="Transactions">
            <div class="table-responsive-wrapper" style="max-height:400px;">
              <table class="modal-table">
                <thead><tr><th>Date</th><th>Invoice</th><th>Location</th><th>Salesperson</th><th>Payment</th><th class="text-right">Amount</th><th class="text-right">SPIFF</th><th class="text-center">Type</th></tr></thead>
                <tbody>
                  <tr v-for="tx in (detailModalData.transactions || [])" :key="tx.invoice_id">
                    <td>{{ formatDate(tx.ts) }}</td>
                    <td>{{ tx.invoice_id }}</td>
                    <td>{{ getLocationName(tx.location_id) }}</td>
                    <td>{{ tx.username }}</td>
                    <td>{{ getPaymentMethodsString(tx.payment_methods) }}</td>
                    <td class="text-right" :class="{ danger: tx.total_dollar_amount < 0 }">{{ formatCurrency(tx.total_dollar_amount) }}</td>
                    <td class="text-right spiff-value" v-if="tx.spiff_commission">{{ formatCurrency(tx.spiff_commission) }}</td>
                    <td class="text-right muted" v-else>-</td>
                    <td class="text-center"><b-badge :variant="tx.is_refund_entry ? 'danger' : 'success'" pill>{{ tx.is_refund_entry ? 'Refund' : 'Sale' }}</b-badge></td>
                  </tr>
                  <tr v-if="!detailModalData.transactions || !detailModalData.transactions.length"><td colspan="8" class="text-center muted">No transactions available</td></tr>
                </tbody>
              </table>
            </div>
          </b-tab>
        </b-tabs>
      </div>
    </b-modal>

    <!-- ===== SALESPERSON DETAIL MODAL ===== -->
    <b-modal v-model="showSalespersonDetail" :title="salespersonDetailTitle" size="lg" hide-footer
             body-class="detail-modal-body" header-class="detail-modal-header">
      <div v-if="isSalespersonDetailLoading" class="modal-loader"><cube-loader small /><p class="loader-text">Loading details...</p></div>
      <div v-else-if="salespersonDetailData">
        <div class="detail-summary-grid">
          <div class="detail-summary-item"><span class="label">Total Sales</span><span class="value">{{ formatCurrency(salespersonDetailData.totalSales) }}</span></div>
          <div class="detail-summary-item"><span class="label">Net Sales</span><span class="value">{{ formatCurrency(salespersonDetailData.netSales) }}</span></div>
          <div class="detail-summary-item"><span class="label">Orders</span><span class="value">{{ salespersonDetailData.orders }}</span></div>
          <div class="detail-summary-item"><span class="label">Avg Order</span><span class="value">{{ formatCurrency(salespersonDetailData.avgOrder) }}</span></div>
          <div class="detail-summary-item spiff-highlight"><span class="label">SPIFF</span><span class="value spiff-value">{{ formatCurrency(salespersonDetailData.totalSpiff) }}</span></div>
        </div>
        <h6 class="section-title">Payment Methods</h6>
        <div class="payment-methods-grid small">
          <div class="payment-method-card small" v-for="pm in salespersonDetailData.paymentMethods" :key="pm.method">
            <div class="payment-method-header"><span class="payment-method-name"><span class="method-dot" :style="{ background: getColor(pm.method) }"></span>{{ pm.method }}</span><span class="payment-method-amount">{{ formatCurrency(pm.amount) }}</span></div>
            <div class="payment-bar"><div class="payment-bar-fill" :style="{ width: pm.percentage + '%', background: getColor(pm.method) }"></div></div>
          </div>
        </div>
        <h6 class="section-title">Transactions</h6>
        <div class="table-responsive-wrapper" style="max-height:300px;">
          <table class="modal-table">
            <thead><tr><th>Date</th><th>Invoice</th><th>Location</th><th>Payment</th><th class="text-right">Amount</th><th class="text-right">SPIFF</th><th class="text-center">Type</th></tr></thead>
            <tbody>
              <tr v-for="tx in salespersonDetailData.transactions" :key="tx.invoice_id">
                <td>{{ formatDate(tx.ts) }}</td><td>{{ tx.invoice_id }}</td><td>{{ getLocationName(tx.location_id) }}</td>
                <td>{{ getPaymentMethodsString(tx.payment_methods) }}</td>
                <td class="text-right" :class="{ danger: tx.total_dollar_amount < 0 }">{{ formatCurrency(tx.total_dollar_amount) }}</td>
                <td class="text-right spiff-value" v-if="tx.spiff_commission">{{ formatCurrency(tx.spiff_commission) }}</td>
                <td class="text-right muted" v-else>-</td>
                <td class="text-center"><b-badge :variant="tx.is_refund_entry ? 'danger' : 'success'" pill>{{ tx.is_refund_entry ? 'Refund' : 'Sale' }}</b-badge></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-modal>

    <!-- ===== DAILY / WEEKLY / MONTHLY VIEW MODAL ===== -->
    <b-modal v-model="showViewModal" :title="viewModalTitle" size="xl" hide-footer
             body-class="view-modal-body" header-class="view-modal-header" @shown="onViewModalShown" @hidden="onViewModalHidden">
      <div v-if="isViewModalLoading" class="modal-loader"><cube-loader small /><p class="loader-text">Loading {{ viewType }} data...</p></div>
      <div v-else-if="viewModalData">
        <div class="view-filters">
          <div class="filters-row">
            <div class="filter-group" v-if="viewType !== 'monthly'"><label>Month</label><b-form-select v-model="viewFilters.month" :options="monthOptions" size="sm" @change="fetchViewData" /></div>
            <div class="filter-group"><label>Year</label><b-form-select v-model="viewFilters.year" :options="yearOptions" size="sm" @change="fetchViewData" /></div>
            <div class="filter-group" v-if="viewType === 'daily'"><label>Week</label><b-form-select v-model="viewFilters.week" :options="weekOptions" size="sm" @change="fetchViewData" /></div>
            <div class="filter-group"><label>Location</label><b-form-select v-model="viewFilters.location" :options="viewLocationOptions" size="sm" @change="fetchViewData" /></div>
            <div class="filter-group"><label>Salesperson</label><b-form-select v-model="viewFilters.salesperson" :options="viewSalespersonOptions" size="sm" @change="fetchViewData" /></div>
            <div class="filter-group filter-actions"><b-button variant="primary" size="sm" @click="fetchViewData" :disabled="isViewModalLoading"><b-icon-search class="mr-1" size="sm" /> Apply</b-button></div>
          </div>
        </div>

        <div class="view-summary-grid">
          <div class="view-summary-item"><span class="label">Total Sales</span><span class="value">{{ formatCurrency(viewModalData.totalSales) }}</span></div>
          <div class="view-summary-item"><span class="label">Net Sales</span><span class="value">{{ formatCurrency(viewModalData.netSales) }}</span></div>
          <div class="view-summary-item"><span class="label">Regular</span><span class="value">{{ formatCurrency(viewModalData.regularSales) }}</span></div>
          <div class="view-summary-item"><span class="label">Layaway</span><span class="value">{{ formatCurrency(viewModalData.layawayTotal) }}</span></div>
          <div class="view-summary-item"><span class="label">Refunds</span><span class="value danger">{{ formatCurrency(viewModalData.totalRefund) }}</span></div>
          <div class="view-summary-item"><span class="label">Orders</span><span class="value">{{ viewModalData.totalOrders }}</span></div>
        </div>

        <div class="spiff-summary-mini" v-if="viewModalData.spiff_summary && viewModalData.spiff_summary.total_spiff_value > 0">
          <div class="spiff-mini-grid">
            <div class="spiff-mini-item"><span class="spiff-mini-label">Total SPIFF</span><span class="spiff-mini-value">{{ formatCurrency(viewModalData.spiff_summary.total_spiff_value) }}</span></div>
            <div class="spiff-mini-item"><span class="spiff-mini-label">SPIFF Invoices</span><span class="spiff-mini-value">{{ viewModalData.spiff_summary.spiff_invoice_count }}</span></div>
            <div class="spiff-mini-item"><span class="spiff-mini-label">% of Sales</span><span class="spiff-mini-value">{{ viewModalData.spiff_summary.spiff_percentage_of_sales || 0 }}%</span></div>
            <div class="spiff-mini-item"><span class="spiff-mini-label">Avg SPIFF</span><span class="spiff-mini-value">{{ formatCurrency(viewModalData.spiff_summary.avg_spiff_per_invoice) }}</span></div>
          </div>
        </div>

        <div class="trend-chart-container">
          <div class="trend-chart-header">
            <h4 class="no-margin"><b-icon-bar-chart-line class="mr-2" />{{ viewType.charAt(0).toUpperCase() + viewType.slice(1) }} Sales Trend</h4>
          </div>
          <div class="trend-chart-wrapper"><canvas id="trendChart"></canvas></div>
          <div class="trend-legend">
            <span class="legend-item"><span class="legend-dot" style="background:#0D4F52"></span>Total Sales</span>
            <span class="legend-item"><span class="legend-dot" style="background:#1E8E5A"></span>Net Sales</span>
            <span class="legend-item"><span class="legend-dot" style="background:#D64545"></span>Refunds</span>
            <span class="legend-item" v-if="viewModalData.spiff_summary && viewModalData.spiff_summary.total_spiff_value > 0"><span class="legend-dot" style="background:#B8862E"></span>SPIFF</span>
          </div>
        </div>

        <div class="table-responsive-wrapper" style="max-height:400px;">
          <table class="modal-table">
            <thead><tr>
              <th>{{ viewType === 'daily' ? 'Day' : viewType === 'weekly' ? 'Week' : 'Month' }}</th>
              <th class="text-right">Total</th><th class="text-right">Net</th><th class="text-right">Regular</th>
              <th class="text-right">Layaway</th><th class="text-right">Layaway Paid</th><th class="text-right">Refunds</th>
              <th class="text-right">Orders</th><th class="text-right">Visits</th><th class="text-right">SPIFF</th>
              <th class="text-right">Avg Order</th><th class="text-center">Action</th>
            </tr></thead>
            <tbody>
              <tr v-for="(item, idx) in viewModalData.data" :key="idx">
                <td><strong>{{ item.label }}</strong></td>
                <td class="text-right">{{ formatCurrency(item.totalSales) }}</td>
                <td class="text-right">{{ formatCurrency(item.netSales) }}</td>
                <td class="text-right">{{ formatCurrency(item.regularSales) }}</td>
                <td class="text-right">{{ formatCurrency(item.layawayTotal) }}</td>
                <td class="text-right">{{ formatCurrency(item.layawayPaid) }}</td>
                <td class="text-right danger">{{ formatCurrency(item.totalRefund) }}</td>
                <td class="text-right">{{ item.orders }}</td>
                <td class="text-right">{{ item.visits || 0 }}</td>
                <td class="text-right spiff-value">{{ formatCurrency(item.totalSpiff) }}</td>
                <td class="text-right">{{ formatCurrency(item.avgOrder) }}</td>
                <td class="text-center"><b-button size="sm" variant="outline-primary" @click="openItemDetail(item, viewType)"><b-icon-search size="sm" /></b-button></td>
              </tr>
            </tbody>
            <tfoot><tr class="table-total"><td><strong>TOTAL</strong></td>
              <td class="text-right"><strong>{{ formatCurrency(viewModalData.totalSales) }}</strong></td>
              <td class="text-right"><strong>{{ formatCurrency(viewModalData.netSales) }}</strong></td>
              <td class="text-right"><strong>{{ formatCurrency(viewModalData.regularSales) }}</strong></td>
              <td class="text-right"><strong>{{ formatCurrency(viewModalData.layawayTotal) }}</strong></td>
              <td class="text-right"><strong>{{ formatCurrency(viewModalData.layawayPaid) }}</strong></td>
              <td class="text-right"><strong>{{ formatCurrency(viewModalData.totalRefund) }}</strong></td>
              <td class="text-right"><strong>{{ viewModalData.totalOrders }}</strong></td>
              <td class="text-right"><strong>{{ viewModalData.totalVisits }}</strong></td>
              <td class="text-right spiff-value"><strong>{{ formatCurrency(viewModalData.totalSpiff) }}</strong></td>
              <td class="text-right"><strong>{{ formatCurrency(viewModalData.avgOrder) }}</strong></td><td></td></tr></tfoot>
          </table>
        </div>
      </div>
    </b-modal>

    <!-- ===== ITEM DETAIL MODAL ===== -->
    <b-modal v-model="showItemDetail" :title="itemDetailTitle" size="xl" hide-footer
             body-class="detail-modal-body" header-class="detail-modal-header">
      <div v-if="isItemDetailLoading" class="modal-loader"><cube-loader small /><p class="loader-text">Loading details...</p></div>
      <div v-else-if="itemDetailData">
        <div class="detail-summary-grid-expanded">
          <div class="detail-summary-item"><span class="label">Total Sales</span><span class="value">{{ formatCurrency(itemDetailData.totalSales) }}</span></div>
          <div class="detail-summary-item"><span class="label">Net Sales</span><span class="value">{{ formatCurrency(itemDetailData.netSales) }}</span></div>
          <div class="detail-summary-item"><span class="label">Regular</span><span class="value">{{ formatCurrency(itemDetailData.regularSales) }}</span></div>
          <div class="detail-summary-item"><span class="label">Layaway Total</span><span class="value">{{ formatCurrency(itemDetailData.layawayTotal) }}</span></div>
          <div class="detail-summary-item"><span class="label">New Layaway</span><span class="value">{{ formatCurrency(itemDetailData.newLayaway) }}</span></div>
          <div class="detail-summary-item"><span class="label">Layaway Paid</span><span class="value">{{ formatCurrency(itemDetailData.layawayPaid) }}</span></div>
          <div class="detail-summary-item"><span class="label">Total Refund</span><span class="value danger">{{ formatCurrency(itemDetailData.totalRefund) }}</span></div>
          <div class="detail-summary-item"><span class="label">Sales Refund</span><span class="value danger">{{ formatCurrency(itemDetailData.salesRefund) }}</span></div>
          <div class="detail-summary-item"><span class="label">Layaway Refund</span><span class="value danger">{{ formatCurrency(itemDetailData.layawayRefund) }}</span></div>
          <div class="detail-summary-item"><span class="label">Orders</span><span class="value">{{ itemDetailData.orders }}</span></div>
          <div class="detail-summary-item"><span class="label">Visits</span><span class="value">{{ itemDetailData.visits }}</span></div>
          <div class="detail-summary-item spiff-highlight"><span class="label">SPIFF</span><span class="value spiff-value">{{ formatCurrency(itemDetailData.totalSpiff) }}</span></div>
        </div>

        <b-tabs pills card class="modal-tabs item-detail-tabs">
          <b-tab title="Payment Methods" active>
            <div class="payment-methods-grid small">
              <div class="payment-method-card small" v-for="pm in itemDetailData.paymentMethods" :key="pm.method">
                <div class="payment-method-header"><span class="payment-method-name"><span class="method-dot" :style="{ background: getColor(pm.method) }"></span>{{ pm.method }}</span><span class="payment-method-amount">{{ formatCurrency(pm.amount) }}</span></div>
                <div class="payment-bar"><div class="payment-bar-fill" :style="{ width: pm.percentage + '%', background: getColor(pm.method) }"></div></div>
                <div class="payment-method-footer"><span>{{ pm.count || 0 }} transactions</span><span>{{ pm.percentage ? pm.percentage.toFixed(1) : 0 }}%</span></div>
              </div>
              <p class="muted-note" v-if="!itemDetailData.paymentMethods || !itemDetailData.paymentMethods.length">No payment data available</p>
            </div>
          </b-tab>

          <b-tab title="Locations">
            <div class="table-responsive-wrapper" style="max-height:350px;">
              <table class="modal-table">
                <thead><tr><th>Location</th><th class="text-right">Total</th><th class="text-right">Net</th><th class="text-right">Regular</th><th class="text-right">Layaway</th><th class="text-right">Layaway Paid</th><th class="text-right">Refunds</th><th class="text-right">Orders</th><th class="text-right">Visits</th><th class="text-right">SPIFF</th></tr></thead>
                <tbody>
                  <tr v-for="loc in itemDetailData.locations" :key="loc.id">
                    <td><strong>{{ loc.name }}</strong></td>
                    <td class="text-right">{{ formatCurrency(loc.total_sales) }}</td>
                    <td class="text-right">{{ formatCurrency(loc.net_sales) }}</td>
                    <td class="text-right">{{ formatCurrency(loc.regular_sales) }}</td>
                    <td class="text-right">{{ formatCurrency(loc.layaway_total) }}</td>
                    <td class="text-right">{{ formatCurrency(loc.layaway_paid) }}</td>
                    <td class="text-right danger">{{ formatCurrency(loc.total_refund) }}</td>
                    <td class="text-right">{{ loc.orders || 0 }}</td>
                    <td class="text-right">{{ loc.visits || 0 }}</td>
                    <td class="text-right spiff-value">{{ formatCurrency(loc.spiff) }}</td>
                  </tr>
                  <tr v-if="!itemDetailData.locations || !itemDetailData.locations.length"><td colspan="10" class="text-center muted">No location data available</td></tr>
                </tbody>
              </table>
            </div>
          </b-tab>

          <b-tab title="Salespeople">
            <div class="table-responsive-wrapper" style="max-height:350px;">
              <table class="modal-table">
                <thead><tr><th>Salesperson</th><th class="text-right">Total</th><th class="text-right">Net</th><th class="text-right">Regular</th><th class="text-right">Layaway</th><th class="text-right">Layaway Paid</th><th class="text-right">Refunds</th><th class="text-right">Orders</th><th class="text-right">SPIFF</th></tr></thead>
                <tbody>
                  <tr v-for="sp in itemDetailData.salespeople" :key="sp.name">
                    <td><strong>{{ sp.name }}</strong></td>
                    <td class="text-right">{{ formatCurrency(sp.total_sales) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.net_sales) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.regular_sales) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.layaway_total) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.layaway_paid) }}</td>
                    <td class="text-right danger">{{ formatCurrency(sp.total_refund) }}</td>
                    <td class="text-right">{{ sp.orders || 0 }}</td>
                    <td class="text-right spiff-value">{{ formatCurrency(sp.spiff) }}</td>
                  </tr>
                  <tr v-if="!itemDetailData.salespeople || !itemDetailData.salespeople.length"><td colspan="9" class="text-center muted">No salesperson data available</td></tr>
                </tbody>
              </table>
            </div>
          </b-tab>

          <b-tab title="Visits">
            <div class="visits-detail-grid">
              <div class="visits-stats-grid">
                <div class="visits-stat-card"><span class="visits-stat-label">Total Visits</span><span class="visits-stat-value">{{ itemDetailData.visits }}</span></div>
                <div class="visits-stat-card"><span class="visits-stat-label">Unique</span><span class="visits-stat-value">{{ itemDetailData.unique_customers }}</span></div>
                <div class="visits-stat-card"><span class="visits-stat-label">Completed</span><span class="visits-stat-value">{{ itemDetailData.completed_visits }}</span></div>
                <div class="visits-stat-card"><span class="visits-stat-label">Active</span><span class="visits-stat-value">{{ itemDetailData.active_visits }}</span></div>
              </div>
              <div class="visits-action-breakdown" v-if="itemDetailData.action_types && Object.keys(itemDetailData.action_types).length">
                <h6 class="section-title">Action Types</h6>
                <div class="action-type-grid">
                  <div class="action-type-card" v-for="(count, type) in itemDetailData.action_types" :key="type">
                    <span class="action-type-name">{{ type.replace('_', ' ').toUpperCase() }}</span>
                    <span class="action-type-count">{{ count }}</span>
                    <div class="action-type-bar"><div class="action-type-bar-fill" :style="{ width: ((count / (itemDetailData.visits || 1)) * 100) + '%' }"></div></div>
                  </div>
                </div>
              </div>
              <div class="table-responsive-wrapper" style="max-height:300px;">
                <table class="modal-table">
                  <thead><tr><th>Name</th><th>Email</th><th>Check In</th><th>Action Type</th></tr></thead>
                  <tbody>
                    <tr v-for="visit in (itemDetailData.visit_list || [])" :key="visit.id">
                      <td>{{ visit.name || 'N/A' }}</td><td>{{ visit.email || 'N/A' }}</td>
                      <td>{{ formatDate(visit.check_in_time) }}</td>
                      <td><b-badge :variant="getActionTypeVariant(visit.action_type)" pill>{{ visit.action_type || 'unknown' }}</b-badge></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import Chart from 'chart.js';

/* Reusable 3D cube loader so markup isn't duplicated five times */
const CubeLoader = {
  name: 'CubeLoader',
  props: { small: { type: Boolean, default: false } },
  template: `
    <div class="loader-3d" :class="{ small }">
      <div class="cube">
        <div class="face front"></div><div class="face back"></div>
        <div class="face right"></div><div class="face left"></div>
        <div class="face top"></div><div class="face bottom"></div>
      </div>
    </div>`
};

const PERIOD_KEYS = ['last_month', 'last_week', 'this_month', 'this_week', 'yesterday', 'today'];
const PERIOD_LABELS = {
  today: 'Today', yesterday: 'Yesterday',
  this_week: 'This Week', last_week: 'Last Week',
  this_month: 'This Month', last_month: 'Last Month'
};
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const PAYMENT_COLORS = {
  CASH: '#B8862E',
  CC: '#1E8E5A',
  'CREDIT CARD': '#1E8E5A',
  CREDIT: '#1E8E5A',
  STORE_CREDIT: '#6B5B95',
  FINANCE: '#2F6FA8',
  'FINANCE COMPANY': '#2F6FA8',
  'TAX EXEMPT': '#0D4F52',
  CHEQUE: '#C7CCD3',
  CHECK: '#C7CCD3',
  OTHER: '#8A93A0'
};
const FINANCE_METHODS = ['ACIMA CREDIT', 'SNAP FINANCE', 'AMERICAN FIRST', 'WELLS FARGO', 'PROGRESSIVE', 'CHECK PERSONAL'];

export default {
  name: 'ReportsSalesStatsPage',
  components: { CubeLoader },
  data() {
    const now = new Date();
    return {
      isLoading: true,
      isRefreshing: false,
      userDetails: {},
      locationOptions: [],
      periodCards: [],
      selectedPeriodKey: 'this_month',

      showDetailModal: false,
      isDetailModalLoading: false,
      detailModalTitle: '',
      detailModalData: null,

      showSalespersonDetail: false,
      isSalespersonDetailLoading: false,
      salespersonDetailTitle: '',
      salespersonDetailData: null,

      showViewModal: false,
      isViewModalLoading: false,
      viewModalTitle: '',
      viewType: 'daily',
      viewModalData: null,
      viewFilters: {
        month: MONTH_NAMES[now.getMonth()],
        year: now.getFullYear(),
        week: 1,
        location: null,
        salesperson: null
      },

      showItemDetail: false,
      isItemDetailLoading: false,
      itemDetailTitle: '',
      itemDetailData: null,

      monthOptions: MONTH_NAMES.map(m => ({ value: m, text: m })),
      yearOptions: [],

      gaugeChart: null,
      trendChart: null,
      periodTrendChart: null,
      bridgeChart: null,

      actionTypeVariants: { purchase: 'success', estimate: 'info', just_looking: 'secondary', unknown: 'light' },
      colors: PAYMENT_COLORS,

      periodDetailsCache: {}
    };
  },
  computed: {
    selectedPeriod() {
      return this.periodCards.find(p => p.key === this.selectedPeriodKey) || null;
    },
    paymentPieTotal() {
      const pms = (this.selectedPeriod && this.selectedPeriod.paymentMethods) || [];
      return pms.reduce((s, pm) => s + (parseFloat(pm.amount) || 0), 0);
    },
    highlightPeriod() {
      return this.periodCards.find(p => p.key === 'this_month') || null;
    },
    weekOptions() {
      const monthIndex = MONTH_NAMES.indexOf(this.viewFilters.month);
      if (monthIndex === -1) return [];
      const year = this.viewFilters.year;
      const firstDay = new Date(year, monthIndex, 1).getDay();
      const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
      const weeks = Math.min(Math.ceil((daysInMonth + firstDay) / 7), 5);
      return Array.from({ length: weeks }, (_, i) => ({ value: i + 1, text: `Week ${i + 1}` }));
    },
    viewLocationOptions() {
      const set = new Set();
      (this.viewModalData.data || []).forEach(item => {
        const raw = item.rawData;
        if (!raw) return;
        if (raw.location_id) set.add(JSON.stringify({ value: raw.location_id, text: this.getLocationName(raw.location_id) }));
        if (raw.location_summary) {
          Object.keys(raw.location_summary).forEach(id =>
            set.add(JSON.stringify({ value: parseInt(id, 10), text: this.getLocationName(parseInt(id, 10)) })));
        }
      });
      return [{ value: null, text: 'All Locations' }, ...Array.from(set).map(s => JSON.parse(s))];
    },
    viewSalespersonOptions() {
      const set = new Set();
      (this.viewModalData.data || []).forEach(item => {
        const raw = item.rawData;
        if (!raw) return;
        if (raw.username) set.add(raw.username);
        if (raw.salesperson_summary) Object.keys(raw.salesperson_summary).forEach(name => set.add(name));
      });
      return [{ value: null, text: 'All Salespeople' }, ...Array.from(set).map(name => ({ value: name, text: name }))];
    }
  },
  mounted() {
    this.loadUserData();
    this.generateYearOptions();
    this.initializeDashboard();
  },
  beforeDestroy() {
    [this.gaugeChart, this.trendChart, this.periodTrendChart, this.bridgeChart].forEach(c => c && c.destroy());
  },
  methods: {
    /* ---------- bootstrap ---------- */
    loadUserData() {
      try {
        this.userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
        const locations = localStorage.getItem('locations');
        if (locations) this.locationOptions = JSON.parse(locations).map(l => ({ value: l.value, text: l.text }));
        const packet = JSON.parse(localStorage.getItem('packet') || '{}');
        if (packet.locations) this.locationOptions = packet.locations.map(l => ({ value: l.value, text: l.text }));
      } catch (e) {
        console.error('Error loading user data:', e);
        this.userDetails = { username: 'Guest' };
      }
    },
    generateYearOptions() {
      const y = new Date().getFullYear();
      for (let i = y - 3; i <= y + 3; i++) this.yearOptions.push({ value: i, text: String(i) });
    },
    async initializeDashboard() {
      this.isLoading = true;
      await this.fetchAllPeriods();
      this.isLoading = false;
      this.$nextTick(() => this.selectPeriod('this_month'));
    },
    async refreshData() {
      this.isRefreshing = true;
      this.periodDetailsCache = {};
      await this.fetchAllPeriods();
      const key = this.selectedPeriodKey;
      const period = this.periodCards.find(p => p.key === key);
      if (period) await this.fetchPeriodDetails(key);
      this.isRefreshing = false;
      this.$bvToast.toast('Data refreshed', { title: 'Success', variant: 'success' });
    },

    /* ---------- period totals (lightweight overview) ---------- */
    async fetchAllPeriods() {
      try {
        const params = { username: this.userDetails.username || 'admin', location_id: null, salesperson_filter: null };
        const { data } = await axios.post('/api/reports/reports_period_totals/', params, { headers: { 'Content-Type': 'application/json' } });
        if (data.result) this.processPeriodTotals(data.result);
        else this.buildEmptyPeriods();
      } catch (error) {
        console.error('Error fetching period totals:', error);
        this.$bvToast.toast('Error loading period data', { title: 'Error', variant: 'danger' });
        this.buildEmptyPeriods();
      }
    },
    processPeriodTotals(data) {
      this.periodCards = PERIOD_KEYS.map(key => {
        const p = data[key] || {};
        return {
          key,
          label: PERIOD_LABELS[key],
          totalSales: p.grossSales || 0,
          netSales: p.netSales || 0,
          regularSales: p.regularSales || 0,
          layawayTotal: p.layawayTotal || 0,
          layawayPaid: p.layawayPaid || 0,
          totalRefund: p.totalRefund || 0,
          salesRefund: 0,
          layawayRefund: 0,
          orders: p.count || 0,
          change: data[`${key}_change`] || null,
          paymentMethods: [],
          visits: { total: p.visits || 0 },
          totalSpiff: 0,
          spiffInvoiceCount: 0,
          detailsLoaded: false,
          loading: false
        };
      });
    },
    buildEmptyPeriods() {
      this.periodCards = PERIOD_KEYS.map(key => ({
        key, label: PERIOD_LABELS[key], totalSales: 0, netSales: 0, regularSales: 0,
        layawayTotal: 0, layawayPaid: 0, totalRefund: 0, salesRefund: 0, layawayRefund: 0,
        orders: 0, change: null, paymentMethods: [], visits: { total: 0 },
        totalSpiff: 0, spiffInvoiceCount: 0, detailsLoaded: false, loading: false
      }));
    },

    /* ---------- period selection + on-demand detail fetch ---------- */
    selectPeriod(key) {
      this.selectedPeriodKey = key;
      const period = this.periodCards.find(p => p.key === key);
      if (!period) return;
      if (!period.detailsLoaded && !period.loading) this.fetchPeriodDetails(key);
      this.$nextTick(() => setTimeout(() => { this.renderPaymentPieChart(); this.renderPeriodTrendChart(); this.renderBridgeBarChart(); }, 250));
    },
    async fetchPeriodDetails(key) {
      const period = this.periodCards.find(p => p.key === key);
      if (!period || period.detailsLoaded) return;

      if (this.periodDetailsCache[key]) {
        const cached = this.periodDetailsCache[key];
        // Backfill trend series if an older cache entry never stored it
        if (!cached.daily_data && !cached.daily_breakdown && !cached.trend_data) {
          period.loading = true;
          try {
            const series = await this.fetchTrendSeriesForPeriod(key);
            if (series && series.length) {
              cached.daily_data = series;
              this.periodDetailsCache[key] = cached;
            }
          } finally {
            period.loading = false;
          }
        }
        this.updatePeriodWithDetails(period, cached);
        return;
      }

      period.loading = true;
      try {
        const params = { username: this.userDetails.username || 'admin', period: key, location_id: null, salesperson: null, include_spiff_details: true };
        const { data } = await axios.post('/api/reports/reports_period_detail/', params, { headers: { 'Content-Type': 'application/json' } });
        if (data.result) {
          // Enrich with day-by-day series for week/month so the trend line has a full timeline
          if (!data.result.daily_data && !data.result.daily_breakdown && !data.result.trend_data) {
            const daily = await this.fetchTrendSeriesForPeriod(key);
            if (daily && daily.length) data.result.daily_data = daily;
          }
          this.periodDetailsCache[key] = data.result;
          this.updatePeriodWithDetails(period, data.result);
        }
      } catch (error) {
        console.error('Error fetching period details:', error);
        this.$bvToast.toast('Error loading details', { title: 'Error', variant: 'danger' });
      } finally {
        period.loading = false;
      }
    },
    async fetchTrendSeriesForPeriod(key) {
      // Build a multi-point series using weekly/monthly report APIs (not a single transaction spike).
      // - this_month / last_month  → reports_weekly → one point per week
      // - this_week / last_week    → reports_daily  → one point per day (falls back to weekly)
      // - today / yesterday        → null (single total is enough)
      if (key === 'today' || key === 'yesterday') return null;

      try {
        const { start, end } = this.getPeriodDateRange(key);
        const monthName = start.format('MMMM');
        const year = start.year();
        const username = this.userDetails.username || 'admin';

        // MONTH periods: weekly breakdown across the month
        if (key === 'this_month' || key === 'last_month') {
          const { data } = await axios.post('/api/reports/reports_weekly/', {
            username,
            view_type: 'weekly',
            month: monthName,
            year,
            week: null,
            location_id: null,
            salesperson: null,
            include_spiff_details: false
          }, { headers: { 'Content-Type': 'application/json' } });

          const weeks = (data && data.result && data.result.weekly_data) || [];
          if (!weeks.length) return null;

          return weeks.map((w, i) => {
            const totals = w.weekly_totals || w.totals || w;
            const weekNum = w.week_number != null ? w.week_number : (i + 1);
            return {
              label: `Week ${weekNum}`,
              date: null,
              total_sales: parseFloat(totals.total_sales) || 0,
              regular_sales: parseFloat(totals.regular_sales) || 0,
              layaway_total: parseFloat(totals.layaway_total) || 0,
              total_refund: parseFloat(totals.total_refund) || 0
            };
          }).filter(r => r.total_sales > 0 || r.regular_sales > 0 || r.total_refund > 0 || true);
        }

        // WEEK periods: try daily report for that week; else one point is fine
        if (key === 'this_week' || key === 'last_week') {
          // week number within the month (1-5)
          const weekNum = Math.min(5, Math.max(1, Math.ceil(start.date() / 7)));
          try {
            const { data } = await axios.post('/api/reports/reports_daily/', {
              username,
              view_type: 'daily',
              month: monthName,
              year,
              week: weekNum,
              location_id: null,
              salesperson: null,
              include_spiff_details: false
            }, { headers: { 'Content-Type': 'application/json' } });

            const days = (data && data.result && data.result.daily_data) || [];
            if (days.length) {
              return days.map((d, i) => {
                let label = d.day_name || d.label || d.date || `Day ${i + 1}`;
                // Prefer real date on axis when available
                const dm = d.date ? moment(d.date) : (d.full_date ? moment(d.full_date) : null);
                if (dm && dm.isValid()) label = dm.format('MMM D');
                else if (d.day_number != null) {
                  const m = start.clone().date(parseInt(d.day_number, 10));
                  if (m.isValid()) label = m.format('MMM D');
                }
                return {
                  label,
                  date: (dm && dm.isValid()) ? dm.format('YYYY-MM-DD') : null,
                  total_sales: parseFloat(d.total_sales) || 0,
                  regular_sales: parseFloat(d.regular_sales) || 0,
                  layaway_total: parseFloat(d.layaway_total) || 0,
                  total_refund: parseFloat(d.total_refund) || 0
                };
              });
            }
          } catch (e) {
            console.warn('reports_daily unavailable for week trend, trying weekly', e);
          }

          // Fallback: weekly report — pick the matching week as a short series if nested daily exists
          const { data: wdata } = await axios.post('/api/reports/reports_weekly/', {
            username,
            view_type: 'weekly',
            month: monthName,
            year,
            week: weekNum,
            location_id: null,
            salesperson: null,
            include_spiff_details: false
          }, { headers: { 'Content-Type': 'application/json' } });

          const weeks = (wdata && wdata.result && wdata.result.weekly_data) || [];
          const match = weeks.find(w => parseInt(w.week_number, 10) === weekNum) || weeks[0];
          if (!match) return null;

          // Some payloads nest daily rows under the week
          const nested = match.daily_data || match.days || match.day_data || [];
          if (nested.length) {
            return nested.map((d, i) => ({
              label: d.day_name || d.label || `Day ${i + 1}`,
              date: d.date || null,
              total_sales: parseFloat(d.total_sales) || 0,
              regular_sales: parseFloat(d.regular_sales) || 0,
              layaway_total: parseFloat(d.layaway_total) || 0,
              total_refund: parseFloat(d.total_refund) || 0
            }));
          }

          const totals = match.weekly_totals || match.totals || match;
          return [{
            label: `Week ${match.week_number || weekNum}`,
            date: null,
            total_sales: parseFloat(totals.total_sales) || 0,
            regular_sales: parseFloat(totals.regular_sales) || 0,
            layaway_total: parseFloat(totals.layaway_total) || 0,
            total_refund: parseFloat(totals.total_refund) || 0
          }];
        }

        return null;
      } catch (e) {
        console.warn('Could not load trend series for period:', e);
        return null;
      }
    },

    updatePeriodWithDetails(period, result) {
      period.paymentMethods = result.paymentMethods || result.paymentStats || [];
      const spiff = result.spiff_summary || {};
      period.totalSpiff = spiff.total_spiff_value || 0;
      period.spiffInvoiceCount = spiff.spiff_invoice_count || 0;
      period.salesRefund = result.salesRefund || 0;
      period.layawayRefund = result.layawayRefund || 0;
      if (!period.visits) period.visits = { total: 0 };
      period.visits.total = (result.visits && result.visits.total) || period.visits.total || 0;
      period.transactions = result.transactions || [];
      // Prefer API daily series when present (full month/week timeline)
      period.dailySeries = result.daily_data || result.daily_breakdown || result.trend_data || result.daily || null;
      period.detailsLoaded = true;

      if (this.selectedPeriodKey === period.key) {
        this.$nextTick(() => setTimeout(() => { this.renderPaymentPieChart(); this.renderPeriodTrendChart(); this.renderBridgeBarChart(); }, 250));
      }
    },

    /* ---------- charts ---------- */
    emptyDoughnut(canvas) {
      return new Chart(canvas, {
        type: 'doughnut',
        data: { labels: ['No Data'], datasets: [{ data: [1], backgroundColor: ['#eef1ef'], borderWidth: 0 }] },
        options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { enabled: false } }, cutout: '78%' }
      });
    },
    /* ---------- period date range helpers ---------- */
    getPeriodDateRange(key) {
      const now = moment().startOf('day');
      let start, end;
      switch (key) {
        case 'today':
          start = now.clone(); end = now.clone();
          break;
        case 'yesterday':
          start = now.clone().subtract(1, 'day'); end = start.clone();
          break;
        case 'this_week':
          start = now.clone().startOf('week'); end = now.clone();
          break;
        case 'last_week':
          start = now.clone().subtract(1, 'week').startOf('week');
          end = start.clone().endOf('week').startOf('day');
          break;
        case 'this_month':
          start = now.clone().startOf('month'); end = now.clone();
          break;
        case 'last_month':
          start = now.clone().subtract(1, 'month').startOf('month');
          end = start.clone().endOf('month').startOf('day');
          break;
        default:
          start = now.clone().startOf('month'); end = now.clone();
      }
      return { start, end };
    },
    buildFullDaySeries(start, end, byDay) {
      const labels = [];
      const total = [];
      const regular = [];
      const layaway = [];
      const refund = [];
      const cursor = start.clone();
      while (cursor.isSameOrBefore(end, 'day')) {
        const key = cursor.format('YYYY-MM-DD');
        const row = byDay[key] || { total: 0, regular: 0, layaway: 0, refund: 0 };
        labels.push(cursor.format('MMM D'));
        total.push(row.total || 0);
        regular.push(row.regular || 0);
        layaway.push(row.layaway || 0);
        refund.push(row.refund || 0);
        cursor.add(1, 'day');
      }
      return { labels, total, regular, layaway, refund };
    },
    parseTxDate(tx) {
      const raw = tx.ts || tx.sale_date || tx.date || tx.created_at || tx.transaction_date;
      if (!raw) return null;
      const m = moment(raw);
      return m.isValid() ? m : null;
    },

    renderPaymentPieChart() {
      const canvas = document.getElementById('payment_pie_selected');
      if (!canvas) return;
      if (this.gaugeChart) { this.gaugeChart.destroy(); this.gaugeChart = null; }

      const period = this.selectedPeriod;
      if (!period || !period.detailsLoaded) return;
      const pms = period.paymentMethods || [];
      if (!pms.length) {
        this.gaugeChart = this.emptyDoughnut(canvas);
        return;
      }

      const sorted = [...pms].sort((a, b) => (b.amount || 0) - (a.amount || 0));
      const top = sorted.slice(0, 7);
      const rest = sorted.slice(7);
      let labels = top.map(p => p.method);
      let values = top.map(p => Math.max(0, parseFloat(p.amount) || 0));
      let colors = top.map(p => this.getColor(p.method));
      if (rest.length) {
        labels.push('Other');
        values.push(rest.reduce((s, p) => s + Math.max(0, parseFloat(p.amount) || 0), 0));
        colors.push('#C7CCD3');
      }

      this.gaugeChart = new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{
            data: values,
            backgroundColor: colors,
            borderColor: '#FFFFFF',
            borderWidth: 0,
            hoverBorderWidth: 0,
            hoverOffset: 6
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '78%',
          rotation: -90,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: 'rgba(20, 24, 31, 0.94)',
              titleFont: { size: 12, weight: '600' },
              bodyFont: { size: 12 },
              padding: 12,
              cornerRadius: 8,
              displayColors: true,
              boxPadding: 4,
              callbacks: {
                label: (ctx) => {
                  const total = ctx.dataset.data.reduce((a, b) => a + b, 0) || 1;
                  const pct = ((ctx.parsed / total) * 100).toFixed(1);
                  return ` ${ctx.label}: ${this.formatCurrency(ctx.parsed)} · ${pct}%`;
                }
              }
            }
          },
          layout: { padding: 4 },
          elements: {
            arc: {
              borderWidth: 0
            }
          }
        }
      });
    },

    renderBridgeBarChart() {
      const canvas = document.getElementById('bridge_bar_chart');
      if (!canvas) return;
      if (this.bridgeChart) { this.bridgeChart.destroy(); this.bridgeChart = null; }

      const period = this.selectedPeriod;
      if (!period || !period.detailsLoaded) return;

      const gross = Math.max(0, parseFloat(period.totalSales) || 0);
      const refunds = Math.max(0, parseFloat(period.totalRefund) || 0);
      const net = Math.max(0, parseFloat(period.netSales) || 0);
      const newLayaway = Math.max(0, (parseFloat(period.layawayTotal) || 0) - (parseFloat(period.layawayPaid) || 0));
      const paidLayaway = Math.max(0, parseFloat(period.layawayPaid) || 0);

      this.bridgeChart = new Chart(canvas, {
        type: 'bar',
        data: {
          labels: ['Gross sales', 'Refunds', 'Net sales', 'New layaway', 'Paid layaway'],
          datasets: [{
            data: [gross, refunds, net, newLayaway, paidLayaway],
            backgroundColor: ['#0D4F52', '#D64545', '#1E8E5A', '#B8862E', '#74BBFB'],
            borderWidth: 0,
            borderRadius: 6,
            maxBarThickness: 48
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: 'rgba(20, 24, 31, 0.94)',
              padding: 12,
              cornerRadius: 8,
              callbacks: {
                label: (ctx) => ' ' + this.formatCurrency(ctx.parsed.y)
              }
            }
          },
          scales: {
            x: {
              grid: { display: false, drawBorder: false },
              ticks: {
                color: '#6B7280',
                font: { size: 11 },
                maxRotation: 0,
                autoSkip: false
              }
            },
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(0,0,0,0.05)', drawBorder: false },
              ticks: {
                color: '#9AA1AC',
                font: { size: 10 },
                maxTicksLimit: 5,
                callback: (v) => this.formatCurrency(v)
              }
            }
          },
          layout: { padding: { top: 8, right: 4, bottom: 0, left: 0 } }
        }
      });
    },

    renderPeriodTrendChart() {
      const period = this.selectedPeriod;
      if (!period) return;
      const canvas = document.getElementById('period_trend_chart_' + period.key);
      if (!canvas) return;
      if (this.periodTrendChart) { this.periodTrendChart.destroy(); this.periodTrendChart = null; }

      const series = period.dailySeries;
      let labels = [];
      let totalData = [];
      let regularData = [];
      let layawayData = [];
      let refundData = [];

      // Prefer report API series (weekly points for month, daily points for week)
      if (Array.isArray(series) && series.length > 0) {
        labels = series.map((d, i) => d.label || d.day_name || d.date || `Point ${i + 1}`);
        totalData = series.map(d => parseFloat(d.total_sales != null ? d.total_sales : d.totalSales) || 0);
        regularData = series.map(d => parseFloat(d.regular_sales != null ? d.regular_sales : d.regularSales) || 0);
        layawayData = series.map(d => parseFloat(d.layaway_total != null ? d.layaway_total : d.layawayTotal) || 0);
        refundData = series.map(d => Math.abs(parseFloat(d.total_refund != null ? d.total_refund : d.totalRefund) || 0));
      } else {
        // Fallback: aggregate transactions — only useful when many txs span many days
        const { start, end } = this.getPeriodDateRange(period.key);
        const transactions = period.detailsLoaded ? (period.transactions || []) : [];
        const byDay = {};
        transactions.forEach(tx => {
          const m = this.parseTxDate(tx);
          if (!m) return;
          const key = m.format('YYYY-MM-DD');
          if (!byDay[key]) byDay[key] = { total: 0, regular: 0, layaway: 0, refund: 0 };
          const amount = parseFloat(tx.total_dollar_amount != null ? tx.total_dollar_amount : tx.amount) || 0;
          byDay[key].total += amount;
          if (tx.is_refund_entry || amount < 0) byDay[key].refund += Math.abs(amount);
          else if (tx.is_layaway || tx.transaction_type === 'layaway' || tx.type === 'layaway') byDay[key].layaway += amount;
          else byDay[key].regular += amount;
        });
        const dayKeys = Object.keys(byDay).sort();
        // If we only have 0–1 days of tx data, show period total as a single honest point
        // instead of a long empty axis with one spike
        if (dayKeys.length <= 1) {
          labels = [period.label || 'Total'];
          totalData = [period.totalSales || (dayKeys[0] ? byDay[dayKeys[0]].total : 0)];
          regularData = [period.regularSales || (dayKeys[0] ? byDay[dayKeys[0]].regular : 0)];
          layawayData = [period.layawayTotal || (dayKeys[0] ? byDay[dayKeys[0]].layaway : 0)];
          refundData = [Math.abs(period.totalRefund || (dayKeys[0] ? byDay[dayKeys[0]].refund : 0))];
        } else {
          const filled = this.buildFullDaySeries(start, end, byDay);
          labels = filled.labels;
          totalData = filled.total;
          regularData = filled.regular;
          layawayData = filled.layaway;
          refundData = filled.refund;
        }
      }

      if (!labels.length) {
        this.periodTrendChart = this.emptyDoughnut(canvas);
        return;
      }

      const mkSet = (data, color, dash) => ({
        data,
        borderColor: color,
        backgroundColor: color + '18',
        fill: true,
        tension: 0.35,
        pointRadius: labels.length > 16 ? 2 : 4,
        pointHoverRadius: 6,
        pointBackgroundColor: color,
        pointBorderColor: '#fff',
        pointBorderWidth: 1,
        borderWidth: 2,
        borderDash: dash || []
      });

      const datasets = [{ ...mkSet(totalData, '#0D4F52'), label: 'Total Sales' }];
      if (regularData.some(v => v > 0)) datasets.push({ ...mkSet(regularData, '#1E8E5A', [4, 4]), label: 'Regular' });
      if (layawayData.some(v => v > 0)) datasets.push({ ...mkSet(layawayData, '#B8862E', [6, 4]), label: 'Layaway' });
      if (refundData.some(v => v > 0)) datasets.push({ ...mkSet(refundData, '#D64545', [8, 4]), label: 'Refunds' });

      this.periodTrendChart = new Chart(canvas, {
        type: 'line',
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: 'rgba(20, 24, 31, 0.92)',
              padding: 10,
              cornerRadius: 8,
              callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${this.formatCurrency(ctx.parsed.y)}`
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: v => this.formatCurrency(v),
                font: { size: 10 },
                maxTicksLimit: 5
              },
              grid: { color: 'rgba(0,0,0,0.05)', drawBorder: false }
            },
            x: {
              ticks: {
                font: { size: 10 },
                maxTicksLimit: 12,
                maxRotation: 0,
                autoSkip: true
              },
              grid: { display: false }
            }
          },
          interaction: { intersect: false, mode: 'index' }
        }
      });
    },

    renderTrendChart() {
      const canvas = document.getElementById('trendChart');
      if (!canvas) return;
      if (this.trendChart) { this.trendChart.destroy(); this.trendChart = null; }

      const rows = this.viewModalData.data || [];
      if (!rows.length) { this.trendChart = this.emptyDoughnut(canvas); return; }

      const labels = rows.map(r => r.label);
      const mk = (field, color, dash) => ({
        data: rows.map(r => Math.abs(r[field] || 0)),
        borderColor: color, backgroundColor: color + '14',
        fill: true, tension: 0.4, pointRadius: 3, pointBackgroundColor: color,
        borderWidth: 2.25, borderDash: dash || []
      });

      const datasets = [
        { ...mk('totalSales', '#0D4F52'), label: 'Total Sales' },
        { ...mk('netSales', '#1E8E5A'), label: 'Net Sales' },
        { ...mk('totalRefund', '#D64545', [5, 5]), label: 'Refunds' }
      ];
      if (rows.some(r => (r.totalSpiff || 0) > 0)) datasets.push({ ...mk('totalSpiff', '#B8862E', [3, 3]), label: 'SPIFF' });

      this.trendChart = new Chart(canvas, {
        type: 'line',
        data: { labels, datasets },
        options: {
          responsive: true, maintainAspectRatio: false,
          interaction: { intersect: false, mode: 'index' },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: 'rgba(11, 32, 34, 0.92)', padding: 10, cornerRadius: 8,
              callbacks: { label: (ctx) => `${ctx.dataset.label}: ${this.formatCurrency(ctx.parsed.y)}` }
            }
          },
          scales: {
            y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.06)', drawBorder: false }, ticks: { font: { size: 10 }, callback: v => this.formatCurrency(v) } },
            x: { grid: { display: false }, ticks: { font: { size: 10, weight: '500' }, color: '#5c6b6c' } }
          },
          animation: { duration: 700, easing: 'easeOutQuart' }
        }
      });
    },

    /* ---------- period detail modal ---------- */
    async openPeriodModal(key) {
      const period = this.periodCards.find(p => p.key === key);
      if (!period) return;
      this.detailModalTitle = `${period.label} - Sales Details`;
      this.showDetailModal = true;
      this.isDetailModalLoading = true;
      if (!period.detailsLoaded) await this.fetchPeriodDetails(key);
      this.loadModalData(key);
    },
    loadModalData(key) {
      const period = this.periodCards.find(p => p.key === key);
      const cached = this.periodDetailsCache[key];
      if (!period) { this.isDetailModalLoading = false; return; }

      const emptySpiff = {
        total_spiff_value: 0, spiff_invoice_count: 0, spiff_percentage_of_sales: 0,
        avg_spiff_per_invoice: 0, unique_spiff_products: 0, salespeople_with_spiffs: 0, spiff_invoice_details: []
      };

      if (cached) {
        this.detailModalData = {
          totalSales: cached.totalSales || cached.grossSales || 0,
          netSales: cached.netSales || 0,
          regularSales: cached.regularSales || 0,
          layawayTotal: cached.layawayTotal || 0,
          layawayPaid: cached.layawayPaid || 0,
          totalRefund: cached.totalRefund || 0,
          salesRefund: cached.salesRefund || 0,
          layawayRefund: cached.layawayRefund || 0,
          totalOrders: cached.totalOrders || cached.count || 0,
          avgOrder: cached.avgOrder || 0,
          totalSpiff: cached.spiff_summary.total_spiff_value || 0,
          totalVisits: cached.visits.total || 0,
          uniqueCustomers: cached.visits.unique_customers || 0,
          completedVisits: cached.visits.completed || 0,
          activeVisits: cached.visits.active || 0,
          actionTypes: cached.visits.action_types || {},
          visitList: cached.visits.list || [],
          paymentMethods: cached.paymentMethods || cached.paymentStats || [],
          salespeople: cached.salespeople || cached.userSales || [],
          locations: cached.locations || cached.locationSales || [],
          transactions: cached.transactions || [],
          spiff_summary: cached.spiff_summary || emptySpiff
        };
      } else {
        this.detailModalData = {
          totalSales: period.totalSales, netSales: period.netSales, regularSales: period.regularSales,
          layawayTotal: period.layawayTotal, layawayPaid: period.layawayPaid, totalRefund: period.totalRefund,
          salesRefund: period.salesRefund || 0, layawayRefund: period.layawayRefund || 0,
          totalOrders: period.orders, avgOrder: period.orders > 0 ? period.totalSales / period.orders : 0,
          totalSpiff: period.totalSpiff || 0, totalVisits: period.visits.total || 0,
          uniqueCustomers: 0, completedVisits: 0, activeVisits: 0, actionTypes: {}, visitList: [],
          paymentMethods: period.paymentMethods || [], salespeople: [], locations: [], transactions: [],
          spiff_summary: {
            ...emptySpiff,
            total_spiff_value: period.totalSpiff || 0,
            spiff_invoice_count: period.spiffInvoiceCount || 0,
            spiff_percentage_of_sales: period.totalSales > 0 ? ((period.totalSpiff || 0) / period.totalSales) * 100 : 0,
            avg_spiff_per_invoice: period.spiffInvoiceCount > 0 ? (period.totalSpiff || 0) / period.spiffInvoiceCount : 0
          }
        };
      }
      this.isDetailModalLoading = false;
    },

    openSalespersonDetail(salesperson) {
      this.salespersonDetailTitle = `${salesperson.name} - Sales Details`;
      this.showSalespersonDetail = true;
      this.isSalespersonDetailLoading = true;

      const transactions = (this.detailModalData.transactions || []).filter(t => t.username === salesperson.name);
      const totalSpiff = transactions.reduce((sum, tx) => sum + (parseFloat(tx.spiff_commission) || 0), 0);

      this.salespersonDetailData = {
        totalSales: salesperson.total_sales || 0,
        netSales: salesperson.net_sales || 0,
        regularSales: salesperson.regular_sales || 0,
        layawayTotal: salesperson.layaway_total || 0,
        layawayPaid: salesperson.layaway_paid || 0,
        totalRefund: salesperson.total_refund || 0,
        orders: salesperson.orders || 0,
        totalSpiff: totalSpiff || salesperson.spiff || 0,
        transactions,
        paymentMethods: this.calculatePaymentMethods(transactions),
        avgOrder: salesperson.orders > 0 ? (salesperson.net_sales || 0) / salesperson.orders : 0
      };
      this.isSalespersonDetailLoading = false;
    },
    calculatePaymentMethods(transactions) {
      const methods = {};
      transactions.forEach(t => {
        let pms = t.payment_methods;
        if (typeof pms === 'string') pms = pms.split(',').map(m => m.trim());
        if (Array.isArray(pms) && pms.length) {
          pms.forEach(m => {
            const amount = Math.abs(t.total_dollar_amount || 0);
            if (!methods[m]) methods[m] = { amount: 0, count: 0 };
            methods[m].amount += amount;
            methods[m].count += 1;
          });
        }
      });
      const total = Object.values(methods).reduce((s, m) => s + m.amount, 0) || 1;
      return Object.keys(methods).map(m => ({ method: m, amount: methods[m].amount, count: methods[m].count, percentage: (methods[m].amount / total) * 100 }));
    },

    /* ---------- daily / weekly / monthly view ---------- */
    openViewModal(viewType) {
      this.viewType = viewType;
      this.viewModalTitle = viewType.charAt(0).toUpperCase() + viewType.slice(1) + ' Sales';
      this.showViewModal = true;

      const now = new Date();
      let currentWeek = Math.min(Math.max(Math.ceil(now.getDate() / 7), 1), 5);
      this.viewFilters = { month: MONTH_NAMES[now.getMonth()], year: now.getFullYear(), week: currentWeek, location: null, salesperson: null };
      this.fetchViewData();
    },
    onViewModalShown() { setTimeout(() => this.renderTrendChart(), 250); },
    onViewModalHidden() { if (this.trendChart) { this.trendChart.destroy(); this.trendChart = null; } },
    async fetchViewData() {
      this.isViewModalLoading = true;
      try {
        const params = {
          username: this.userDetails.username || 'admin', view_type: this.viewType,
          month: this.viewFilters.month, year: this.viewFilters.year, week: this.viewFilters.week,
          location_id: this.viewFilters.location, salesperson: this.viewFilters.salesperson, include_spiff_details: true
        };
        const { data } = await axios.post(`/api/reports/reports_${this.viewType}/`, params, { headers: { 'Content-Type': 'application/json' } });
        if (data.result) {
          this.viewModalData = this.processViewData(data.result);
          this.$nextTick(() => setTimeout(() => this.renderTrendChart(), 150));
        } else {
          this.viewModalData = null;
          this.$bvToast.toast('No data found for the selected filters', { title: 'Info', variant: 'info' });
        }
      } catch (error) {
        console.error('Error fetching view data:', error);
        this.viewModalData = null;
        this.$bvToast.toast('Error loading data', { title: 'Error', variant: 'danger' });
      } finally {
        this.isViewModalLoading = false;
      }
    },
    processViewData(data) {
      let items = [];
      let totals = {};
      const spiffSummary = data.spiff_summary || {};

      if (this.viewType === 'daily') {
        items = (data.daily_data || []).map(d => ({
          label: d.day_name, totalSales: d.total_sales, netSales: d.net_sales, regularSales: d.regular_sales,
          layawayTotal: d.layaway_total, layawayPaid: d.layaway_paid, totalRefund: d.total_refund,
          salesRefund: d.sales_refund, layawayRefund: d.layaway_refund, orders: d.order_count, avgOrder: d.avg_order,
          visits: d.visits || 0, totalSpiff: d.total_spiff || 0, rawData: d
        }));
        totals = data.weekly_totals || {};
      } else if (this.viewType === 'weekly') {
        const allWeeks = (data.weekly_data || []).map(w => ({
          label: `Week ${w.week_number}`, totalSales: w.weekly_totals.total_sales, netSales: w.weekly_totals.net_sales,
          regularSales: w.weekly_totals.regular_sales, layawayTotal: w.weekly_totals.layaway_total, layawayPaid: w.weekly_totals.layaway_paid,
          totalRefund: w.weekly_totals.total_refund, salesRefund: w.weekly_totals.sales_refund, layawayRefund: w.weekly_totals.layaway_refund,
          orders: w.weekly_totals.order_count, avgOrder: w.weekly_totals.avg_order, visits: w.weekly_totals.visits || 0,
          totalSpiff: w.weekly_totals.total_spiff || 0, rawData: w
        }));
        const currentWeek = this.viewFilters.week || 1;
        items = allWeeks.filter(w => {
          const num = parseInt(w.label.split(' ')[1], 10);
          return (w.totalSales > 0 || w.orders > 0 || num <= currentWeek) && num <= 5;
        });
        totals = data.monthly_totals || {};
      } else {
        items = (data.monthly_data || []).map(m => ({
          label: m.month, totalSales: m.monthly_totals.total_sales, netSales: m.monthly_totals.net_sales,
          regularSales: m.monthly_totals.regular_sales, layawayTotal: m.monthly_totals.layaway_total, layawayPaid: m.monthly_totals.layaway_paid,
          totalRefund: m.monthly_totals.total_refund, salesRefund: m.monthly_totals.sales_refund, layawayRefund: m.monthly_totals.layaway_refund,
          orders: m.monthly_totals.order_count, avgOrder: m.monthly_totals.avg_order, visits: m.monthly_totals.visits || 0,
          totalSpiff: m.monthly_totals.total_spiff || 0, rawData: m
        }));
        totals = data.yearly_totals || {};
      }

      return {
        data: items,
        totalSales: totals.total_sales || 0, netSales: totals.net_sales || 0, regularSales: totals.regular_sales || 0,
        layawayTotal: totals.layaway_total || 0, layawayPaid: totals.layaway_paid || 0, totalRefund: totals.total_refund || 0,
        salesRefund: totals.sales_refund || 0, layawayRefund: totals.layaway_refund || 0,
        totalOrders: totals.order_count || 0, avgOrder: totals.avg_order || 0,
        totalVisits: data.total_visits || 0, totalSpiff: spiffSummary.total_spiff_value || 0,
        spiff_summary: spiffSummary, rawData: data
      };
    },

    /* ---------- item (day/week/month) detail modal ---------- */
    openItemDetail(item, viewType) {
      this.itemDetailTitle = `${item.label} - Sales Details`;
      this.showItemDetail = true;
      this.isItemDetailLoading = true;

      const rawData = item.rawData || {};
      const fullData = this.viewModalData.rawData || {};
      let locations = [], salespeople = [], paymentMethods = [];

      if (Array.isArray(rawData.payment_methods)) paymentMethods = rawData.payment_methods;
      else if (Array.isArray(rawData.paymentMethods)) paymentMethods = rawData.paymentMethods;

      const extractPayments = (pmData) => {
        if (!pmData || typeof pmData !== 'object') return [];
        const total = Object.values(pmData).reduce((s, p) => s + (p.amount || 0), 0);
        return Object.keys(pmData).map(k => ({ method: k, amount: pmData[k].amount || 0, count: pmData[k].count || 0, percentage: total > 0 ? ((pmData[k].amount || 0) / total) * 100 : 0 }));
      };
      if (!paymentMethods.length && fullData.payment_methods) paymentMethods = extractPayments(fullData.payment_methods);

      const extractLocations = (summary) => !summary ? [] : Object.keys(summary).map(k => ({
        id: k, name: this.getLocationName(parseInt(k, 10)),
        total_sales: summary[k].total_sales || 0, net_sales: summary[k].net_sales || 0, regular_sales: summary[k].regular_sales || 0,
        layaway_total: summary[k].layaway_total || 0, layaway_paid: summary[k].layaway_paid || 0, total_refund: summary[k].total_refund || 0,
        orders: summary[k].order_count || 0, visits: summary[k].visits || 0, spiff: summary[k].spiff || 0
      }));
      const extractSalespeople = (summary) => !summary ? [] : Object.keys(summary).map(k => ({
        name: k, total_sales: summary[k].total_sales || 0, net_sales: summary[k].net_sales || 0, regular_sales: summary[k].regular_sales || 0,
        layaway_total: summary[k].layaway_total || 0, layaway_paid: summary[k].layaway_paid || 0, total_refund: summary[k].total_refund || 0,
        orders: summary[k].order_count || 0, spiff: summary[k].spiff || 0
      }));

      if (viewType === 'daily') {
        locations = extractLocations(fullData.location_summary);
        salespeople = extractSalespeople(fullData.salesperson_summary);
      } else if (viewType === 'weekly') {
        const weekData = (fullData.weekly_data || []).find(w => w.week_number === parseInt(item.label.split(' ')[1], 10));
        if (weekData) {
          locations = extractLocations(weekData.location_summary);
          salespeople = extractSalespeople(weekData.salesperson_summary);
        }
      } else if (viewType === 'monthly') {
        const monthData = (fullData.monthly_data || []).find(m => m.month === item.label);
        if (monthData) {
          const locMap = {}, spMap = {};
          const addLoc = (dst, src) => {
            Object.keys(src || {}).forEach(k => {
              if (!dst[k]) dst[k] = { total_sales: 0, net_sales: 0, regular_sales: 0, layaway_total: 0, layaway_paid: 0, total_refund: 0, order_count: 0, visits: 0, spiff: 0 };
              ['total_sales', 'net_sales', 'regular_sales', 'layaway_total', 'layaway_paid', 'total_refund', 'order_count', 'visits', 'spiff'].forEach(f => { dst[k][f] += src[k][f] || 0; });
            });
          };
          const addSp = (dst, src) => {
            Object.keys(src || {}).forEach(k => {
              if (!dst[k]) dst[k] = { total_sales: 0, net_sales: 0, regular_sales: 0, layaway_total: 0, layaway_paid: 0, total_refund: 0, order_count: 0, spiff: 0 };
              ['total_sales', 'net_sales', 'regular_sales', 'layaway_total', 'layaway_paid', 'total_refund', 'order_count', 'spiff'].forEach(f => { dst[k][f] += src[k][f] || 0; });
            });
          };
          (monthData.weekly_data || []).forEach(week => { addLoc(locMap, week.location_summary); addSp(spMap, week.salesperson_summary); });
          if (monthData.payment_methods) paymentMethods = extractPayments(monthData.payment_methods);
          locations = Object.keys(locMap).map(k => ({ id: k, name: this.getLocationName(parseInt(k, 10)), total_sales: locMap[k].total_sales, net_sales: locMap[k].net_sales, regular_sales: locMap[k].regular_sales, layaway_total: locMap[k].layaway_total, layaway_paid: locMap[k].layaway_paid, total_refund: locMap[k].total_refund, orders: locMap[k].order_count, visits: locMap[k].visits, spiff: locMap[k].spiff }));
          salespeople = Object.keys(spMap).map(k => ({ name: k, total_sales: spMap[k].total_sales, net_sales: spMap[k].net_sales, regular_sales: spMap[k].regular_sales, layaway_total: spMap[k].layaway_total, layaway_paid: spMap[k].layaway_paid, total_refund: spMap[k].total_refund, orders: spMap[k].order_count, spiff: spMap[k].spiff }));
        }
      }

      locations.sort((a, b) => b.total_sales - a.total_sales);
      salespeople.sort((a, b) => b.total_sales - a.total_sales);

      this.itemDetailData = {
        totalSales: item.totalSales || 0, netSales: item.netSales || 0, regularSales: item.regularSales || 0,
        layawayTotal: item.layawayTotal || 0, newLayaway: (item.layawayTotal || 0) - (item.layawayPaid || 0), layawayPaid: item.layawayPaid || 0,
        totalRefund: item.totalRefund || 0, salesRefund: item.salesRefund || 0, layawayRefund: item.layawayRefund || 0,
        orders: item.orders || 0, avgOrder: item.avgOrder || 0, visits: item.visits || 0, totalSpiff: item.totalSpiff || 0,
        paymentMethods, salespeople, locations,
        action_types: rawData.action_types || {}, unique_customers: rawData.unique_customers || 0,
        completed_visits: rawData.completed_visits || 0, active_visits: rawData.active_visits || 0, visit_list: rawData.visit_list || []
      };
      this.isItemDetailLoading = false;
    },

    /* ---------- helpers ---------- */
    trendClass(change) { return change !== null && change < 0 ? 'down' : 'up'; },
    avgOrderValue(period) { return period.orders > 0 ? this.formatCurrency(period.totalSales / period.orders) : '$0.00'; },
    refundRate(period) { return period.totalSales > 0 ? ((period.totalRefund / period.totalSales) * 100).toFixed(2) : '0.00'; },
    spiffShare(period) { return period.totalSales > 0 ? ((period.totalSpiff / period.totalSales) * 100).toFixed(1) : 0; },
    getLocationName(locationId) {
      if (!locationId) return 'Unknown';
      if (!this.locationOptions.length) return `Location ${locationId}`;
      const loc = this.locationOptions.find(l => l.value == locationId); // eslint-disable-line eqeqeq
      return loc ? loc.text : `Location ${locationId}`;
    },
    getColor(method) {
      if (FINANCE_METHODS.includes(method)) return this.colors.FINANCE;
      return this.colors[method] || this.colors.OTHER;
    },
    getActionTypeVariant(type) { return this.actionTypeVariants[type] || 'secondary'; },
    getPaymentMethodsString(pm) {
      if (!pm) return '-';
      if (Array.isArray(pm)) return pm.join(', ');
      return typeof pm === 'string' ? pm : '-';
    },
    formatCurrency(value) {
      if (value === undefined || value === null) return '$0.00';
      return `$${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    formatDate(ts) { return ts ? moment(ts).format('MM-DD-YYYY HH:mm') : '-'; }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=Manrope:wght@400;500;600;700;800&display=swap');

/* ═══════════════════════════════════════════════════════
   REPORTSMYSALE DESIGN LANGUAGE
   Warm cream background · orange accents · Sora/Manrope
   ═══════════════════════════════════════════════════════ */
.sales-dashboard {
  --bg: #F6F4F0;
  --panel: #FFFFFF;
  --border: #E5E1DA;
  --border-soft: #F1EEE9;
  --ink: #221F1C;
  --ink-soft: #736C63;
  --ink-faint: #9A9490;
  --orange: #EE7A22;
  --orange-hover: #D4620F;
  --orange-soft: #FFF1E4;
  --orange-border: #F5CFAE;
  --teal: #0F6E6B;
  --teal-soft: #E2F2F0;
  --coral: #C93C30;
  --coral-soft: #FBE6E3;
  --blue: #2D6A9D;
  --amber: #C98A12;
  --amber-soft: #FFF8F0;
  --gray: #8A93A0;
  --gray-soft: #F7F5F2;
  --num: 'Sora', sans-serif;
  --body: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  --shadow-sm: 0 1px 2px rgba(34, 31, 28, 0.04);
  --shadow-md: 0 2px 12px rgba(34, 31, 28, 0.06);
  --shadow-lg: 0 14px 40px rgba(34, 31, 28, 0.12);

  margin: 0;
  padding: 18px 0 60px;
  background: var(--bg);
  min-height: 100vh;
  color: var(--ink);
  font-family: var(--body);
  font-size: 15px;
  -webkit-font-smoothing: antialiased;
}

.sales-dashboard > * {
  max-width: 1600px;
  width: 97%;
  margin-left: auto;
  margin-right: auto;
}

/* ═══════════ LOADER ═══════════ */
.global-loader {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 80vh;
}
.loader-3d { width: 60px; height: 60px; perspective: 600px; }
.loader-3d.small { width: 38px; height: 38px; }
.cube {
  width: 100%; height: 100%; position: relative; transform-style: preserve-3d;
  animation: rotateCube 2s infinite ease-in-out;
}
@keyframes rotateCube {
  0%   { transform: rotateX(-30deg) rotateY(0deg); }
  50%  { transform: rotateX(-30deg) rotateY(180deg); }
  100% { transform: rotateX(-30deg) rotateY(360deg); }
}
.cube .face {
  position: absolute; width: 100%; height: 100%;
  border: 1.5px solid rgba(238, 122, 34, 0.3);
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(238, 122, 34, 0.88), rgba(212, 98, 15, 0.88));
  box-shadow: 0 0 24px rgba(238, 122, 34, 0.2);
}
.cube .face.front  { transform: translateZ(30px); }
.cube .face.back   { transform: rotateY(180deg) translateZ(30px); }
.cube .face.right  { transform: rotateY(90deg) translateZ(30px); }
.cube .face.left   { transform: rotateY(-90deg) translateZ(30px); }
.cube .face.top    { transform: rotateX(90deg) translateZ(30px); }
.cube .face.bottom { transform: rotateX(-90deg) translateZ(30px); }
.loader-3d.small .face.front  { transform: translateZ(19px); }
.loader-3d.small .face.back   { transform: rotateY(180deg) translateZ(19px); }
.loader-3d.small .face.right  { transform: rotateY(90deg) translateZ(19px); }
.loader-3d.small .face.left   { transform: rotateY(-90deg) translateZ(19px); }
.loader-3d.small .face.top    { transform: rotateX(90deg) translateZ(19px); }
.loader-3d.small .face.bottom { transform: rotateX(-90deg) translateZ(19px); }
.loader-text {
  margin-top: 22px; color: var(--ink-soft); font-size: 14px; font-weight: 600;
  animation: pulseText 1.5s ease-in-out infinite;
}
@keyframes pulseText { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }
.spin { animation: spinIcon 0.8s linear infinite; }
@keyframes spinIcon { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* ═══════════ HERO HEADER ═══════════ */
.dash-hero {
  background: var(--ink);
  color: #fff;
  border-radius: 20px;
  overflow: hidden;
  border-bottom: 5px solid var(--orange);
  margin-bottom: 18px;
  animation: dashSlideDown 0.5s ease-out;
}
@keyframes dashSlideDown {
  from { opacity: 0; transform: translateY(-12px); }
  to   { opacity: 1; transform: translateY(0); }
}
.dash-hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(0, 1fr);
  gap: 24px;
  padding: 22px 30px;
  align-items: center;
}
.dash-hero-title {
  font-family: var(--num);
  font-size: 28px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: 0.02em;
}
.dash-hero-sub {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.65);
  margin-top: 6px;
}
.dash-hero-side {
  display: flex; justify-content: flex-end; align-items: center; gap: 14px;
  flex-wrap: wrap;
}
.dash-hero-badge {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
  padding: 7px 16px;
  border-radius: 999px;
  font-size: 13px; font-weight: 700;
  border: 1px solid rgba(52, 211, 153, 0.3);
}
.badge-dot {
  width: 8px; height: 8px; background: #34d399; border-radius: 50%;
  animation: pulseDot 2s infinite;
}
@keyframes pulseDot { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
.dash-hero-user {
  display: flex; align-items: center; gap: 8px;
  color: rgba(255, 255, 255, 0.85); font-size: 14px; font-weight: 600;
  background: rgba(255, 255, 255, 0.06);
  padding: 7px 16px; border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

/* ═══════════ MAIN ═══════════ */
.main { padding: 0 0 24px; }
.breadcrumb { font-size: 12.5px; color: var(--ink-faint); margin-bottom: 6px; font-weight: 600; }
.breadcrumb span { color: var(--ink-soft); }

.topbar {
  display: flex; align-items: flex-end; justify-content: space-between;
  margin-bottom: 22px; flex-wrap: wrap; gap: 14px;
}
.topbar h1 {
  font-family: var(--num);
  font-size: 24px; font-weight: 800; margin: 0;
  letter-spacing: -0.015em; color: var(--ink);
}
.controls { display: flex; gap: 10px; align-items: center; }
.btn {
  border: 1.5px solid transparent; border-radius: 10px; padding: 10px 18px;
  font-family: var(--body); font-weight: 700; font-size: 14px; cursor: pointer;
  display: inline-flex; align-items: center; gap: 8px;
  transition: all 0.15s ease;
}
.btn svg { width: 14px; height: 14px; }
.btn-primary {
  background: var(--orange); color: #fff; border-color: var(--orange);
}
.btn-primary:hover:not(:disabled) { background: var(--orange-hover); border-color: var(--orange-hover); transform: translateY(-1px); }
.btn-ghost {
  background: #fff; border-color: var(--border); color: var(--ink);
}
.btn-ghost:hover:not(:disabled) { border-color: var(--orange); color: var(--orange-hover); }
.btn:disabled { opacity: 0.55; cursor: default; }

/* ═══════════ KPI GRID (6 across) ═══════════ */
.kpi-grid {
  display: grid; grid-template-columns: repeat(6, 1fr);
  gap: 14px; margin-bottom: 28px;
}
.kpi {
  background: #fff; border: 1.5px solid var(--border); border-radius: 14px;
  padding: 16px 18px; box-shadow: var(--shadow-sm);
  cursor: pointer; transition: all 0.2s ease;
  border-top: 4px solid transparent;
}
.kpi:hover { border-color: var(--ink-faint); border-top-color: var(--orange); box-shadow: var(--shadow-md); transform: translateY(-2px); }
.kpi.primary {
  border-color: var(--orange);
  border-top-color: var(--orange);
  background: linear-gradient(135deg, #FFFBF6, #FFF1E4);
  box-shadow: 0 4px 16px rgba(238, 122, 34, 0.15);
}
.kpi.primary .label { color: var(--orange-hover); font-weight: 800; }
.kpi.primary .amount { color: var(--orange-hover); }
.kpi .label {
  font-size: 12px; color: var(--ink-soft); font-weight: 700;
  margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em;
}
.kpi .amtrow {
  display: flex; align-items: baseline; justify-content: space-between; gap: 8px;
}
.kpi .amount {
  font-family: var(--num); font-size: 20px; font-weight: 800;
  letter-spacing: -0.02em; color: var(--ink);
}
.kpi .trend {
  font-size: 11px; font-weight: 800; display: flex; align-items: center;
  gap: 2px; flex-shrink: 0; padding: 3px 8px; border-radius: 999px;
}
.trend.down { color: var(--coral); background: var(--coral-soft); }
.trend.up { color: var(--teal); background: var(--teal-soft); }
.kpi-meta { font-size: 11.5px; color: var(--ink-faint); margin-top: 8px; font-weight: 600; }

/* ═══════════ SECTION HEAD ═══════════ */
.section-head {
  display: flex; align-items: baseline; justify-content: space-between;
  margin-bottom: 14px;
}
.section-title {
  font-family: var(--num);
  font-size: 18px; font-weight: 800; margin: 0;
  letter-spacing: -0.01em; color: var(--ink);
}
.section-sub { font-size: 13px; color: var(--ink-soft); margin: 3px 0 0; font-weight: 600; }
.section-link {
  font-size: 13.5px; font-weight: 800; color: var(--orange-hover);
  cursor: pointer; transition: transform 0.2s;
}
.section-link:hover { transform: translateX(3px); color: var(--orange); }

/* ═══════════ GRID3 panels ═══════════ */
.grid3 {
  display: grid; grid-template-columns: 0.95fr 0.85fr 1fr;
  gap: 16px; margin-bottom: 16px;
}
.panel {
  background: #fff; border: 1.5px solid var(--border); border-radius: 16px;
  padding: 20px; box-shadow: var(--shadow-sm);
  transition: box-shadow 0.2s;
}
.panel:hover { box-shadow: var(--shadow-md); }
.panel-title {
  font-family: var(--num);
  font-size: 14px; font-weight: 800; color: var(--ink);
  margin: 0 0 3px; letter-spacing: 0.01em;
}
.panel-sub { font-size: 12px; color: var(--ink-faint); margin: 0 0 16px; font-weight: 600; }

.trend-wrap { display: flex; flex-direction: column; }
.trend-chart-wrapper.main-trend { height: 210px; position: relative; width: 100%; }
.trend-chart-wrapper.main-trend canvas { width: 100% !important; height: 100% !important; }
.trend-stats {
  display: flex; gap: 20px; padding-top: 14px;
  border-top: 1px solid var(--border-soft); margin-top: 10px;
}
.trend-stat .k { font-size: 11px; color: var(--ink-soft); margin-bottom: 3px; font-weight: 700; }
.trend-stat .v { font-family: var(--num); font-weight: 800; font-size: 15px; color: var(--ink); }
.trend-stat .v.coral { color: var(--coral); }
.trend-legend { display: flex; gap: 14px; margin-top: 12px; flex-wrap: wrap; }
.legend-item {
  font-size: 12px; color: var(--ink-soft); display: flex;
  align-items: center; gap: 6px; font-weight: 600;
}
.legend-dot { width: 9px; height: 9px; border-radius: 2px; display: inline-block; }

/* Invoice list */
.invoice-list { display: flex; flex-direction: column; }
.invoice-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 0; border-bottom: 1px solid var(--border-soft); font-size: 13.5px;
}
.invoice-row:first-child { padding-top: 0; }
.invoice-row:last-child { border-bottom: none; padding-bottom: 0; }
.invoice-row .name { font-weight: 700; color: var(--ink); }
.invoice-row .name.muted { font-weight: 500; color: var(--ink-faint); }
.invoice-row .meta { color: var(--ink-faint); font-size: 11.5px; margin-top: 2px; font-weight: 600; }
.invoice-row .val { font-family: var(--num); font-weight: 800; font-size: 14px; color: var(--ink); }
.invoice-row .val.danger { color: var(--coral); }
.invoice-row .val.spiff { color: var(--orange-hover); }

/* Stat list */
.stat-list { display: flex; flex-direction: column; }
.stat-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 11px 0; border-bottom: 1px solid var(--border-soft);
}
.stat-row:first-child { padding-top: 0; }
.stat-row:last-child { border-bottom: none; padding-bottom: 0; }
.stat-row .k { font-size: 13px; color: var(--ink-soft); font-weight: 600; }
.stat-row .k.muted { color: var(--ink-faint); }
.stat-row .v { font-family: var(--num); font-weight: 800; font-size: 14px; color: var(--ink); }
.stat-row .v.danger { color: var(--coral); }
.stat-row .v.spiff { color: var(--orange-hover); }

/* ═══════════ BREAKDOWN GRID ═══════════ */
.breakdown-grid {
  display: grid; grid-template-columns: 1.15fr 1fr;
  gap: 16px; margin-bottom: 20px;
}
.breakdown {
  background: #fff; border: 1.5px solid var(--border); border-radius: 16px;
  padding: 20px; box-shadow: var(--shadow-sm);
  transition: box-shadow 0.2s;
}
.breakdown:hover { box-shadow: var(--shadow-md); }
.breakdown table { width: 100%; border-collapse: collapse; }
.breakdown thead th {
  text-align: left; font-size: 11px; text-transform: uppercase;
  letter-spacing: 0.06em; color: var(--ink-faint); font-weight: 800;
  padding: 0 0 10px; border-bottom: 2px solid var(--border);
}
.breakdown thead th:not(:first-child) { text-align: right; }
.breakdown tbody td {
  padding: 12px 0; border-bottom: 1px solid var(--border-soft); font-size: 13.5px;
}
.breakdown tbody td:not(:first-child) { text-align: right; font-family: var(--num); font-weight: 700; }
.breakdown tbody tr:last-child td { border-bottom: none; }
.breakdown tbody tr:hover td { background: #FFF8F0; }
.row-label { display: flex; align-items: center; gap: 10px; font-weight: 700; }
.dot { width: 9px; height: 9px; border-radius: 2px; flex: none; display: inline-block; }

.payment-pie-layout {
  display: grid; grid-template-columns: 220px 1fr; gap: 24px;
  align-items: center; margin: 4px 0 18px;
}
.payment-pie-chart-wrap {
  width: 220px; height: 220px; position: relative; margin: 0 auto;
}
.payment-pie-chart-wrap canvas { width: 100% !important; height: 100% !important; }
.pie-center {
  position: absolute; inset: 0; display: flex;
  flex-direction: column; align-items: center; justify-content: center;
  pointer-events: none; text-align: center; padding: 24px;
}
.pie-center-label {
  font-size: 11px; font-weight: 800; color: var(--ink-faint);
  text-transform: uppercase; letter-spacing: 0.06em;
  margin-bottom: 4px; margin-top: 35px;
}
.pie-center-value {
  font-family: var(--num); font-size: 14px; font-weight: 800;
  color: var(--ink); letter-spacing: -0.02em; line-height: 1.2;
  max-width: 110px; word-break: break-word;
}
.tender-legend-grid .leg-name {
  flex: 1; min-width: 0; overflow: hidden;
  text-overflow: ellipsis; white-space: nowrap;
}
.tender-legend-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; margin-bottom: 18px;
}
.tender-legend-grid .item {
  display: flex; align-items: center; gap: 8px;
  font-size: 12.5px; color: var(--ink); font-weight: 600;
}
.tender-legend-grid .pct {
  margin-left: auto; font-family: var(--num); font-weight: 800;
  color: var(--ink-soft); font-size: 12px;
}

.bridge-chart-wrap {
  height: 200px; width: 100%; position: relative; margin: 4px 0 12px;
}
.bridge-chart-wrap canvas { width: 100% !important; height: 100% !important; }
.bridge-legend {
  display: flex; flex-wrap: wrap; gap: 12px 16px; margin-bottom: 14px;
}
.bridge-numbers {
  display: grid; grid-template-columns: 1fr 1fr 1fr;
  gap: 12px; margin: 8px 0 4px;
}
.bridge-col {
  text-align: center; padding: 14px 10px;
  background: var(--gray-soft); border-radius: 10px;
  border: 1px solid var(--border-soft);
}
.bridge-col.mid { background: var(--coral-soft); border-color: #F8D8D5; }
.bridge-label { font-size: 11px; color: var(--ink-soft); font-weight: 800; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.04em; }
.bridge-label.danger { color: var(--coral); }
.bridge-value { font-family: var(--num); font-weight: 800; font-size: 16px; color: var(--ink); }
.bridge-value.danger { color: var(--coral); }
.bridge-value.net { color: var(--teal); }

.pipeline-row {
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
  margin-top: 14px; padding-top: 16px; border-top: 1px solid var(--border); width: 100%;
}
.pipeline-card {
  background: var(--gray-soft); border-radius: 10px; padding: 12px 14px;
  border: 1px solid var(--border-soft);
}
.pipeline-card .k { font-size: 11px; color: var(--ink-soft); font-weight: 800; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.04em; }
.pipeline-card .v { font-family: var(--num); font-weight: 800; font-size: 15px; color: var(--ink); }
.pipeline-card .note { font-size: 11px; color: var(--ink-faint); margin-top: 5px; line-height: 1.4; font-weight: 500; }

.muted-note { color: var(--ink-faint); font-size: 13.5px; padding: 24px 0; text-align: center; font-weight: 600; }

/* ═══════════ MAIN CARDS (quick nav) ═══════════ */
.main-cards-grid {
  display: grid; grid-template-columns: repeat(3, 1fr);
  gap: 16px; margin-top: 8px;
}
.main-card {
  background: #fff; border-radius: 16px; padding: 20px 24px;
  display: flex; align-items: center; gap: 16px;
  box-shadow: var(--shadow-sm); cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1.5px solid var(--border);
}
.main-card:hover {
  border-color: var(--orange);
  box-shadow: var(--shadow-lg);
  transform: translateY(-4px);
}
.main-card-icon {
  width: 48px; height: 48px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; flex-shrink: 0;
}
.main-card-icon.daily   { background: linear-gradient(135deg, #EE7A22, #D4620F); }
.main-card-icon.weekly  { background: linear-gradient(135deg, #0F6E6B, #0B5552); }
.main-card-icon.monthly { background: linear-gradient(135deg, #C98A12, #A87208); }
.main-card-content h6 {
  margin: 0; font-family: var(--num);
  font-weight: 800; color: var(--ink); font-size: 15px;
}
.main-card-content p { margin: 3px 0 0; font-size: 12.5px; color: var(--ink-soft); font-weight: 500; }
.main-card-cta {
  display: inline-block; font-size: 12.5px;
  color: var(--orange-hover); font-weight: 800;
  margin-top: 5px; transition: transform 0.25s; letter-spacing: 0.02em;
}
.main-card:hover .main-card-cta { transform: translateX(5px); color: var(--orange); }

/* ═══════════ MODALS ═══════════ */
.detail-modal-header, .view-modal-header {
  background: var(--ink) !important;
  color: #fff !important;
  border-bottom: 4px solid var(--orange) !important;
  padding: 15px 26px !important;
}
.detail-modal-header .modal-title, .view-modal-header .modal-title {
  font-family: var(--num) !important;
  font-size: 16px !important;
  font-weight: 800 !important;
  color: #FFB06B !important;
  letter-spacing: 0.02em;
}
.detail-modal-header .close, .view-modal-header .close {
  color: #fff !important;
  opacity: 0.85;
  text-shadow: none;
}
.detail-modal-header .close:hover, .view-modal-header .close:hover {
  opacity: 1; transform: rotate(90deg);
}
.detail-modal-body, .view-modal-body {
  padding: 20px 24px 24px !important;
  background: var(--bg) !important;
  max-height: calc(100vh - 140px);
  overflow-y: auto;
}
.modal-loader {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 48px 20px;
}

/* Modal shell */
.sales-dashboard >>> .modal-content {
  border: 1px solid var(--border);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(34, 31, 28, 0.2);
}
.sales-dashboard >>> .modal-xl { max-width: 1150px; }
.sales-dashboard >>> .modal-lg { max-width: 820px; }
.sales-dashboard >>> .modal-header { align-items: center; }
.sales-dashboard >>> .modal-header .close {
  margin: 0; padding: 4px 8px; font-size: 22px; line-height: 1;
}

/* Modal summary */
.modal-summary-grid {
  display: grid; grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px; margin-bottom: 18px;
}
.modal-summary-item {
  background: #fff; padding: 12px 14px;
  border-radius: 12px; display: flex; align-items: flex-start; gap: 10px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border);
  border-left: 4px solid transparent;
  min-width: 0;
}
.modal-summary-item:nth-child(1) { border-left-color: var(--orange); }
.modal-summary-item:nth-child(2) { border-left-color: var(--teal); }
.modal-summary-item:nth-child(3) { border-left-color: var(--blue); }
.modal-summary-item:nth-child(4) { border-left-color: var(--amber); }
.modal-summary-item:nth-child(5) { border-left-color: var(--coral); }
.modal-summary-item:nth-child(6) { border-left-color: #8A5CC7; }

.modal-summary-item > div:last-child {
  min-width: 0; overflow: hidden;
}
.summary-icon {
  width: 36px; height: 36px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 16px; flex-shrink: 0;
}
.modal-summary-item .value {
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%;
}
.summary-icon.total    { background: linear-gradient(135deg, #EE7A22, #D4620F); }
.summary-icon.net      { background: linear-gradient(135deg, #0F6E6B, #0B5552); }
.summary-icon.regular  { background: linear-gradient(135deg, #3F8AC9, #2D6A9D); }
.summary-icon.layaway  { background: linear-gradient(135deg, #E0A524, #C98A12); }
.summary-icon.refund   { background: linear-gradient(135deg, #C93C30, #A22A1F); }
.summary-icon.orders   { background: linear-gradient(135deg, #8A5CC7, #6A3FA5); }
.modal-summary-item .label {
  display: block; font-size: 10px; color: var(--ink-soft);
  text-transform: uppercase; letter-spacing: 0.06em; font-weight: 800;
}
.modal-summary-item .value {
  display: block; font-family: var(--num);
  font-size: 15px; font-weight: 800; color: var(--ink); line-height: 1.25;
}
.modal-summary-item .value.danger { color: var(--coral); }
.modal-summary-item small { font-size: 10px; color: var(--ink-faint); font-weight: 600; }

/* Tabs */
.modal-tabs { background: transparent; border: none; box-shadow: none; }
.modal-tabs >>> .card-header {
  background: #fff !important;
  border: 1px solid var(--border) !important;
  border-radius: 12px !important;
  padding: 6px !important;
  margin-bottom: 14px;
}
.modal-tabs >>> .nav { flex-wrap: wrap; gap: 4px; border: none !important; }
.modal-tabs >>> .nav-pills .nav-link,
.modal-tabs >>> .nav-tabs .nav-link {
  border: none !important; color: var(--ink-soft);
  font-family: var(--num); font-weight: 700;
  padding: 9px 16px; border-radius: 8px;
  font-size: 12.5px; white-space: nowrap;
}
.modal-tabs >>> .nav-pills .nav-link:hover,
.modal-tabs >>> .nav-tabs .nav-link:hover {
  color: var(--orange-hover); background: var(--orange-soft);
}
.modal-tabs >>> .nav-pills .nav-link.active,
.modal-tabs >>> .nav-tabs .nav-link.active {
  background: var(--orange) !important;
  color: #fff !important;
  box-shadow: 0 4px 12px rgba(238, 122, 34, 0.25);
}
.modal-tabs >>> .card-body { padding: 0 !important; background: transparent !important; }
.modal-tabs >>> .tab-content { padding: 0; background: transparent; }
.modal-tabs >>> .tab-pane { outline: none; }

/* Payment methods */
.payment-methods-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: 12px;
}
.payment-methods-grid.small { grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); }
.payment-method-card {
  background: #fff; border-radius: 12px;
  padding: 14px 16px; border: 1px solid var(--border);
  box-shadow: var(--shadow-sm); min-width: 0;
  transition: box-shadow 0.2s;
}
.payment-method-card:hover { box-shadow: var(--shadow-md); }
.payment-method-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
.payment-method-name {
  font-family: var(--num);
  font-weight: 800; font-size: 13.5px;
  display: flex; align-items: center; gap: 7px; color: var(--ink);
}
.method-dot { width: 9px; height: 9px; border-radius: 2px; display: inline-block; }
.payment-method-amount { font-family: var(--num); font-weight: 800; font-size: 13.5px; color: var(--ink); }
.payment-bar { height: 5px; background: var(--gray-soft); border-radius: 4px; margin: 6px 0; overflow: hidden; }
.payment-bar-fill { height: 100%; border-radius: 4px; }
.payment-method-footer {
  display: flex; justify-content: space-between;
  font-size: 11.5px; color: var(--ink-soft); margin-top: 3px; font-weight: 700;
}
.sub-methods { margin-top: 6px; padding: 6px 10px; background: var(--gray-soft); border-radius: 8px; }
.sub-method {
  display: flex; justify-content: space-between;
  font-size: 11.5px; padding: 2px 0; color: var(--ink-soft); font-weight: 600;
}

/* Tables - reportsmysale style */
.table-responsive-wrapper {
  overflow-x: auto; max-height: 380px; overflow-y: auto;
  border-radius: 12px; background: #fff;
  border: 1px solid var(--border);
  -webkit-overflow-scrolling: touch;
}
.modal-table {
  width: 100%; font-size: 13px;
  border-collapse: separate; border-spacing: 0; margin: 0;
}
.modal-table th, .modal-table td {
  padding: 11px 14px;
  border-bottom: 1px solid var(--border-soft);
  white-space: nowrap; vertical-align: middle;
}
.modal-table th {
  background: var(--ink); color: #fff;
  font-family: var(--num);
  font-weight: 800; font-size: 11px;
  text-transform: uppercase; letter-spacing: 0.06em;
  position: sticky; top: 0; z-index: 2;
  border-bottom: 3px solid var(--orange);
}
.modal-table td { color: var(--ink); font-weight: 500; font-size: 13px; }
.modal-table td:not(:first-child) { font-family: var(--num); font-weight: 700; }
.modal-table tbody tr:hover td { background: #FFF8F0; }
.modal-table .table-total {
  background: var(--ink);
}
.modal-table .table-total td {
  border-top: 0; border-bottom: 0;
  padding: 12px 14px;
  font-family: var(--num); font-weight: 800;
  color: #FFB06B;
}
.modal-table .table-total strong { color: #FFB06B; }

.text-right  { text-align: right; }
.text-center { text-align: center; }
.danger      { color: var(--coral) !important; }
.muted       { color: var(--ink-faint) !important; }
.spiff-value { color: var(--orange-hover) !important; font-weight: 800; font-family: var(--num); }

/* View modal filters */
.view-filters {
  background: #fff; border-radius: 12px;
  padding: 16px 20px; margin-bottom: 14px;
  box-shadow: var(--shadow-sm); border: 1px solid var(--border);
}
.filters-row { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.filter-group { display: flex; flex-direction: column; min-width: 120px; flex: 1; }
.filter-group label {
  font-size: 11px; font-weight: 800; color: var(--ink-soft);
  text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px;
}
.filter-group .form-control, .filter-group .custom-select {
  border-radius: 9px; font-size: 13px; font-weight: 600;
  background: var(--gray-soft); border-color: var(--border);
  color: var(--ink); height: 40px;
}
.filter-group .form-control:focus, .filter-group .custom-select:focus {
  border-color: var(--orange); box-shadow: none;
}
.filter-group.filter-actions { flex: 0 0 auto; }

.view-summary-grid {
  display: grid; grid-template-columns: repeat(6, 1fr);
  gap: 10px; margin-bottom: 14px;
}
.view-summary-item {
  background: #fff; padding: 12px 14px; border-radius: 12px;
  text-align: left; border: 1px solid var(--border);
  box-shadow: var(--shadow-sm); border-top: 3px solid var(--orange);
}
.view-summary-item:nth-child(2) { border-top-color: var(--teal); }
.view-summary-item:nth-child(3) { border-top-color: var(--blue); }
.view-summary-item:nth-child(4) { border-top-color: var(--amber); }
.view-summary-item:nth-child(5) { border-top-color: var(--coral); }
.view-summary-item:nth-child(6) { border-top-color: #8A5CC7; }
.view-summary-item .label {
  display: block; font-size: 10.5px; color: var(--ink-soft);
  text-transform: uppercase; letter-spacing: 0.06em; font-weight: 800;
}
.view-summary-item .value {
  display: block; font-family: var(--num);
  font-size: 16px; font-weight: 800; color: var(--ink); margin-top: 2px;
}
.view-summary-item .value.danger { color: var(--coral); }

/* SPIFF blocks */
.spiff-summary-mini {
  background: linear-gradient(135deg, #FFF8F0, #FFE8CC);
  border-radius: 12px; padding: 12px 16px; margin-bottom: 14px;
  border: 1.5px solid var(--orange-border);
}
.spiff-mini-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.spiff-mini-item { text-align: left; }
.spiff-mini-label {
  display: block; font-size: 10px; color: var(--amber);
  text-transform: uppercase; letter-spacing: 0.06em; font-weight: 800;
}
.spiff-mini-value {
  display: block; font-family: var(--num);
  font-size: 14.5px; font-weight: 800; color: var(--orange-hover); margin-top: 2px;
}
.spiff-summary-cards {
  display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; margin-bottom: 16px;
}
.spiff-card {
  background: #fff; padding: 12px; border-radius: 10px;
  text-align: left; border: 1.5px solid var(--orange-border);
}
.spiff-card-label {
  display: block; font-size: 10px; color: var(--amber);
  text-transform: uppercase; letter-spacing: 0.06em; font-weight: 800;
}
.spiff-card-value {
  display: block; font-family: var(--num);
  font-size: 16px; font-weight: 800; color: var(--orange-hover); margin-top: 2px;
}
.spiff-highlight {
  background: linear-gradient(135deg, #FFF8F0, #FFE8CC) !important;
  border-left-color: var(--orange) !important;
}

/* Detail summary grids */
.detail-summary-grid {
  display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin-bottom: 16px;
}
.detail-summary-item {
  background: #fff; padding: 12px 14px; border-radius: 10px;
  text-align: left; border: 1px solid var(--border);
  box-shadow: var(--shadow-sm); border-left: 3px solid var(--orange);
}
.detail-summary-item .label {
  display: block; font-size: 10.5px; color: var(--ink-soft);
  text-transform: uppercase; letter-spacing: 0.06em; font-weight: 800;
}
.detail-summary-item .value {
  display: block; font-family: var(--num);
  font-size: 15px; font-weight: 800; color: var(--ink); margin-top: 2px;
}
.detail-summary-item .value.danger { color: var(--coral); }
.detail-summary-grid-expanded {
  display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; margin-bottom: 16px;
}
.detail-summary-grid-expanded .detail-summary-item {
  border-left: 0; border-top: 3px solid var(--orange);
}
.detail-summary-grid-expanded .detail-summary-item:nth-child(7),
.detail-summary-grid-expanded .detail-summary-item:nth-child(8),
.detail-summary-grid-expanded .detail-summary-item:nth-child(9) {
  border-top-color: var(--coral);
}
.detail-summary-grid-expanded .detail-summary-item.spiff-highlight {
  border-top-color: var(--orange) !important;
  background: linear-gradient(135deg, #FFF8F0, #FFE8CC) !important;
}

/* Visits */
.visits-detail-grid { display: flex; flex-direction: column; gap: 16px; }
.visits-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.visits-stat-card {
  background: #fff; padding: 12px 14px; border-radius: 10px;
  text-align: left; border: 1px solid var(--border);
  box-shadow: var(--shadow-sm); border-top: 3px solid var(--orange);
}
.visits-stat-card:nth-child(2) { border-top-color: var(--teal); }
.visits-stat-card:nth-child(3) { border-top-color: var(--blue); }
.visits-stat-card:nth-child(4) { border-top-color: var(--amber); }
.visits-stat-label {
  display: block; font-size: 10.5px; color: var(--ink-soft);
  text-transform: uppercase; letter-spacing: 0.06em; font-weight: 800;
}
.visits-stat-value {
  display: block; font-family: var(--num);
  font-size: 18px; font-weight: 800; color: var(--ink); margin-top: 2px;
}
.visits-action-breakdown {
  background: #fff; border-radius: 10px;
  padding: 14px 16px; border: 1px solid var(--border);
}
.action-type-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px;
}
.action-type-card {
  background: var(--gray-soft); border-radius: 10px; padding: 10px 12px;
  display: flex; flex-direction: column; gap: 4px;
  border: 1px solid var(--border-soft);
}
.action-type-name {
  font-size: 11px; font-weight: 800; color: var(--ink-soft);
  text-transform: uppercase; letter-spacing: 0.05em;
}
.action-type-count { font-family: var(--num); font-size: 18px; font-weight: 800; color: var(--ink); }
.action-type-bar { height: 4px; background: var(--border); border-radius: 4px; overflow: hidden; margin-top: 2px; }
.action-type-bar-fill {
  height: 100%; border-radius: 4px;
  background: linear-gradient(135deg, #EE7A22, #D4620F);
}

h6.section-title {
  font-family: var(--num);
  font-size: 14px; font-weight: 800; color: var(--ink);
  margin: 16px 0 10px; padding-bottom: 8px;
  border-bottom: 2px solid var(--orange);
  letter-spacing: 0.02em;
}

/* Trend chart in view modal */
.trend-chart-container {
  background: #fff; border: 1px solid var(--border);
  border-radius: 14px; padding: 18px 22px;
  margin-bottom: 14px; box-shadow: var(--shadow-sm);
}
.trend-chart-container h4 {
  font-family: var(--num);
  font-size: 15px; font-weight: 800; color: var(--ink);
}
.no-margin { margin-bottom: 14px !important; }
.trend-chart-wrapper { height: 230px; width: 100%; position: relative; }

/* Buttons inside modals get orange treatment */
.sales-dashboard >>> .btn-outline-primary {
  color: var(--orange-hover) !important;
  border-color: var(--orange) !important;
  font-weight: 700;
}
.sales-dashboard >>> .btn-outline-primary:hover {
  background: var(--orange) !important;
  color: #fff !important;
}
.sales-dashboard >>> .btn-primary {
  background: var(--orange) !important;
  border-color: var(--orange) !important;
}
.sales-dashboard >>> .btn-primary:hover:not(:disabled) {
  background: var(--orange-hover) !important;
  border-color: var(--orange-hover) !important;
}

/* ═══════════ RESPONSIVE ═══════════ */
@media (max-width: 1180px) {
  .kpi-grid { grid-template-columns: repeat(3, 1fr); }
  .grid3 { grid-template-columns: 1fr; }
  .breakdown-grid { grid-template-columns: 1fr; }
  .modal-summary-grid, .view-summary-grid, .spiff-summary-cards { grid-template-columns: repeat(3, 1fr); }
  .payment-pie-layout { grid-template-columns: 200px 1fr; }
  .payment-pie-chart-wrap { width: 200px; height: 200px; }
}
@media (max-width: 900px) {
  .dash-hero-grid { grid-template-columns: 1fr; gap: 14px; padding: 18px 22px; }
  .dash-hero-title { font-size: 22px; }
  .dash-hero-side { justify-content: flex-start; }
}
@media (max-width: 768px) {
  .sales-dashboard { padding: 10px 0 40px; }
  .sales-dashboard > * { width: 100%; padding-left: 12px; padding-right: 12px; }
  .payment-pie-layout { grid-template-columns: 1fr; justify-items: center; }
  .payment-pie-chart-wrap { width: 200px; height: 200px; }
  .modal-summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
  .detail-modal-body, .view-modal-body { padding: 14px 16px 20px !important; }
  .main { padding: 20px 12px 40px; }
  .kpi-grid { grid-template-columns: repeat(2, 1fr); }
  .main-cards-grid { grid-template-columns: 1fr; }
  .topbar { flex-direction: column; align-items: flex-start; }
  .modal-summary-grid, .view-summary-grid { grid-template-columns: repeat(2, 1fr); }
  .visits-stats-grid, .detail-summary-grid { grid-template-columns: repeat(2, 1fr); }
  .filters-row { flex-direction: column; }
  .filter-group { min-width: 100%; }
  .bridge-numbers { grid-template-columns: 1fr; }
  .trend-stats { flex-wrap: wrap; gap: 12px; }
}
@media (max-width: 480px) {
  .kpi .amount { font-size: 17px; }
  .modal-summary-grid, .view-summary-grid, .payment-methods-grid,
  .visits-stats-grid, .detail-summary-grid, .spiff-summary-cards,
  .detail-summary-grid-expanded { grid-template-columns: 1fr; }
  .tender-legend-grid { grid-template-columns: 1fr; }
}
</style>


// WEBPACK FOOTER //
// src/components/Reports/ReportsSalesStatsPage.vue